import EntityManager from "@/components/EntityManager";
import { VehicleVaultDialog } from "@/components/VehicleVaultDialog";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, AlertTriangle, MessageCircle, IndianRupee, Trash2, BellRing, Download, BookOpen, Paperclip, Eye, StickyNote, CheckCircle2 } from "lucide-react";
import { inr } from "@/lib/format";
import { fmtDate, today, parseTermDate, parseTermMonth } from "@/lib/format";
import { printChallan, printChallans, printVehiclePayment, printVehiclePaymentRegister, printReminderRegister, printBalanceDueRegister, printSettlementRegister, printPodAckRegister, printVendorAckScorecard, printNoteVoucher, printOldVehicleRegister, printRouteRateRegister, printExtraChargesReport, printChargeDecisions, printYearlyCharges } from "@/lib/print";
import { logPadIssue, PadLogButton } from "@/components/PadIssueLog";
import { HEAD_OFFICE } from "@/lib/company";
import { api, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

function PodAckCell({ row, onChanged }) {
  const [busy, setBusy] = useState(false);
  const chn = String(row.challan_no || "").replace(/^VHC[-\s]?/i, "");
  const mark = async (received) => {
    setBusy(true);
    try {
      await api.post(`/hire-challans/${row.id}/pod-ack`, { received });
      toast.success(received ? `Challan ${chn} — signed POD marked back` : `Challan ${chn} — POD ack removed`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  if (row.pod_ack_received) {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700" title={`Signed POD back on ${fmtDate(row.pod_ack_date)}${row.pod_ack_by ? ` · by ${row.pod_ack_by}` : ""}`} data-testid="challan-pod-ack-badge">
          Back ✓ {fmtDate(row.pod_ack_date)}
        </span>
        <button type="button" className="px-0.5 text-[11px] font-bold text-slate-400 hover:text-red-600" title="Undo POD ack" disabled={busy} onClick={() => mark(false)} data-testid="challan-pod-ack-undo">✕</button>
      </span>
    );
  }
  const ph = String(row.driver_phone || row.vendor_mobile || row.owner_mobile || "").replace(/\D/g, "");
  const ld = String(row.loading_date || row.date || "").slice(0, 10);
  const outDays = /^\d{4}-\d{2}-\d{2}$/.test(ld) ? Math.floor((new Date(today()) - new Date(ld)) / 86400000) : 0;
  const aged = outDays > 15;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
      <span
        className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${aged ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}
        title={aged ? `Signed POD out for ${outDays} days since loading (over 15 days) — chase the driver` : "Signed POD not yet back from the driver"}
        data-testid={aged ? "challan-pod-ack-aged" : "challan-pod-ack-out"}
      >
        {aged ? `Out ${outDays}d ⚠` : "Out"}
      </span>
      <button type="button" disabled={busy} className="rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-100" title="Mark signed POD received back from driver" onClick={() => mark(true)} data-testid="challan-pod-ack-mark-btn">
        ✓ Back
      </button>
      {ph ? (
        <button
          type="button"
          className="grid h-6 w-6 place-items-center rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
          title="WhatsApp chase: return the signed POD"
          onClick={() => {
            const msg = [
              "*NTC — Signed POD Required*",
              `Challan ${chn || "—"} · Vehicle ${row.vehicle_no || "—"}`,
              `Route: ${row.origin || "—"} → ${row.destination || "—"}`,
              "",
              "Please return the signed POD / delivery acknowledgement to the branch immediately. Balance payment is released only after the signed POD is received.",
              "— Nisha Transport Corporation",
            ].join("\n");
            window.open(`https://wa.me/91${ph.slice(-10)}?text=${encodeURIComponent(msg)}`, "_blank");
          }}
          data-testid="challan-pod-ack-chase-btn"
        >
          <MessageCircle className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </span>
  );
}

function PodCell({ row, onChanged }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const url = row.pod_path ? `${API}/files/${row.pod_path}?auth=${localStorage.getItem("ntc_token")}` : "";
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/hire-challans/${row.id}/pod`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("POD attached — payment release is proof-backed");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <span className="inline-flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
      {row.pod_path ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex h-7 w-7 items-center justify-center rounded text-emerald-600 hover:bg-emerald-50"
          title={`View POD (${row.pod_filename || "file"})`}
          data-testid="challan-pod-view"
        >
          <Eye className="h-4 w-4" />
        </a>
      ) : null}
      <button
        type="button"
        disabled={busy}
        onClick={() => ref.current?.click()}
        className={`inline-flex h-7 w-7 items-center justify-center rounded hover:bg-slate-100 ${row.pod_path ? "text-emerald-600" : "text-slate-400"}`}
        title={row.pod_path ? "Replace signed POD" : "Attach signed POD (proof for payment release)"}
        data-testid="challan-pod-upload"
      >
        <Paperclip className="h-4 w-4" />
      </button>
      <input ref={ref} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="challan-pod-input" />
    </span>
  );
}

const CHALLAN_COPIES = ["LORRY COPY", "ACCOUNT COPY", "HO COPY", "FILE COPY"];

const PAY_TYPES = ["Vehicle Hire Payment", "Vehicle Advance Payment", "Vehicle Balance Payment"];
const PAY_MODES = ["Cash", "Bank", "UPI", "NEFT", "RTGS", "IMPS", "Cheque"];

function VehiclePaymentsRegisterDialog({ open, onClose }) {
  const [entries, setEntries] = useState(null);
  const [challans, setChallans] = useState([]);
  const [tab, setTab] = useState("payments");
  const [owner, setOwner] = useState("All Owners");
  const [brFil, setBrFil] = useState("All");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  useEffect(() => {
    if (!open) return;
    setOwner("All Owners");
    setFrom("");
    setTo("");
    setTab("payments");
    api.get("/hire-challans").then((r) => {
      setChallans(r.data || []);
      const list = [];
      (r.data || []).forEach((c) => {
        (c.payments || []).forEach((p) => {
          list.push({
            id: p.id,
            date: p.date || "",
            challan_no: c.challan_no || "",
            vehicle_no: c.vehicle_no || "",
            owner: c.owner_name || c.vendor_broker_name || "—",
            branch: c.engaged_office || c.office || "",
            type: p.type,
            mode: p.mode,
            amount: Number(p.amount) || 0,
            by: p.by || "",
            unloading: Number(c.unloading_charge) || 0,
            challan_chg: Number(c.challan_charge) || 0,
            extra_wt: Number(c.extra_weight_charge) || 0,
            extra_dly: Number(c.extra_delivery_charge) || 0,
            charge_reason: c.extra_charge_reason || "",
          });
        });
      });
      list.sort((a, b) => (a.date < b.date ? 1 : -1));
      setEntries(list);
    }).catch(() => setEntries([]));
  }, [open]);
  const owners = ["All Owners", ...[...new Set((entries || []).map((e) => e.owner).filter((o) => o && o !== "—"))].sort()];
  const filtered = (entries || []).filter(
    (e) => (owner === "All Owners" || e.owner === owner) && (brFil === "All" || (e.branch || "") === brFil) && (!from || e.date >= from) && (!to || e.date <= to)
  );
  const total = filtered.reduce((a, e) => a + e.amount, 0);
  const ownerSummary = (() => {
    const map = {};
    challans.forEach((c) => {
      const o = c.owner_name || c.vendor_broker_name || "—";
      if (owner !== "All Owners" && o !== owner) return;
      const d = String(c.loading_date || c.date || "").slice(0, 10);
      if (from && d && d < from) return;
      if (to && d && d > to) return;
      map[o] = map[o] || { challans: 0, hire: 0, paid: 0, extras: 0 };
      map[o].challans += 1;
      map[o].hire += Number(c.hire_amount) || 0;
      map[o].paid += (c.payments || []).reduce((a, p) => a + (Number(p.amount) || 0), 0);
      map[o].extras += (Number(c.unloading_charge) || 0) + (Number(c.challan_charge) || 0) + (Number(c.extra_weight_charge) || 0) + (Number(c.extra_delivery_charge) || 0);
    });
    return Object.entries(map).map(([o, m]) => ({ owner: o, ...m, pending: Math.max(m.hire - m.paid, 0) })).sort((a, b) => b.pending - a.pending);
  })();
  const downloadRegisterCsv = () => {
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const lines = [["Date", "Challan No.", "Vehicle No.", "Owner / Broker", "Payment", "Mode", "Unloading Charge", "Challan Charge", "Extra Weight Charge", "Extra Delivery Charge", "Charges Reason", "Amount", "By"].join(",")];
    filtered.forEach((e) => lines.push([e.date || "", String(e.challan_no || "").replace(/^VHC[-\s]?/i, ""), e.vehicle_no || "", e.owner, e.type, e.mode || "", e.unloading || 0, e.challan_chg || 0, e.extra_wt || 0, e.extra_dly || 0, e.charge_reason || "", e.amount, e.by || ""].map(cell).join(",")));
    lines.push(["TOTAL", "", "", "", "", "", "", "", "", "", "", total, ""].map(cell).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vehicle-payment-register-${today()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  const downloadBranchSplitXlsx = async () => {
    const XLSX = await import("xlsx");
    const header = ["Date", "Challan No.", "Vehicle No.", "Owner / Broker", "Payment", "Mode", "Unloading Charge", "Challan Charge", "Extra Weight Charge", "Extra Delivery Charge", "Charges Reason", "Amount", "By"];
    const wb = XLSX.utils.book_new();
    const branches = [...new Set(filtered.map((e) => e.branch || "Unassigned"))].sort();
    branches.forEach((b) => {
      const rows = filtered.filter((e) => (e.branch || "Unassigned") === b);
      const aoa = [
        header,
        ...rows.map((e) => [e.date || "", String(e.challan_no || "").replace(/^VHC[-\s]?/i, ""), e.vehicle_no || "", e.owner, e.type, e.mode || "", e.unloading || 0, e.challan_chg || 0, e.extra_wt || 0, e.extra_dly || 0, e.charge_reason || "", e.amount, e.by || ""]),
        ["TOTAL", "", "", "", "", "", "", "", "", "", "", rows.reduce((a, e) => a + e.amount, 0), ""],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), b.slice(0, 31));
    });
    XLSX.writeFile(wb, `vehicle-payment-register-by-branch-${today()}.xlsx`);
    toast.success(`Excel downloaded — one sheet per branch (${branches.join(", ")})`);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto" data-testid="vehicle-payments-register-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-8">
            <span>Vehicle Payments Register</span>
            <span className="flex gap-2">
              <Button size="sm" variant="outline" onClick={downloadRegisterCsv} disabled={!filtered.length} data-testid="vehicle-payments-excel-btn">
                Excel ↓
              </Button>
              <Button size="sm" variant="outline" onClick={downloadBranchSplitXlsx} disabled={!filtered.length} title="One Excel file with a separate sheet for each branch's payments" data-testid="vehicle-payments-branch-excel-btn">
                Excel by Branch ↓
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => printVehiclePaymentRegister(filtered, { owner: owner === "All Owners" ? "" : owner, from, to })}
                disabled={!filtered.length}
                data-testid="vehicle-payments-print-btn"
              >
                <Printer className="mr-1.5 h-3.5 w-3.5" /> Print Register
              </Button>
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-1.5">
          <button type="button" onClick={() => setTab("payments")} className={`rounded-full px-3 py-1 text-xs font-semibold ${tab === "payments" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="vp-tab-payments">
            Payments
          </button>
          <button type="button" onClick={() => setTab("summary")} className={`rounded-full px-3 py-1 text-xs font-semibold ${tab === "summary" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="vp-tab-summary">
            Owner Summary
          </button>
          <span className="ml-auto flex flex-wrap items-center gap-1.5">
            {[...new Set((entries || []).map((e) => e.branch).filter(Boolean))].sort().map((b) => {
              const n = (entries || []).filter((e) => (e.branch || "") === b).length;
              const active = brFil === b;
              return (
                <button
                  type="button"
                  key={b}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                  title={active ? "Tap to show all branches again" : `Tap to show only ${b}'s payments`}
                  onClick={() => setBrFil(active ? "All" : b)}
                  data-testid={`vp-branch-count-${b.toLowerCase()}`}
                >
                  {b} <b className={active ? "" : "text-primary"}>{n}</b>
                </button>
              );
            })}
          </span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="grid gap-1">
            <Label className="text-xs">Vehicle Owner / Broker</Label>
            <Select value={owner} onValueChange={setOwner}>
              <SelectTrigger className="h-9 w-[220px]" data-testid="vehicle-payments-owner-select"><SelectValue /></SelectTrigger>
              <SelectContent>{owners.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">From</Label>
            <Input type="date" className="h-9 w-[150px]" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="vehicle-payments-from" />
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">To</Label>
            <Input type="date" className="h-9 w-[150px]" value={to} onChange={(e) => setTo(e.target.value)} data-testid="vehicle-payments-to" />
          </div>
          <span className="ml-auto pb-2 text-xs font-semibold text-slate-600" data-testid="vehicle-payments-total">
            {filtered.length} payment(s) · Total {inr(total)}
          </span>
        </div>
        {tab === "summary" ? (
          ownerSummary.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No challans found.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs" data-testid="owner-summary-table">
                <thead className="bg-slate-50">
                  <tr className="[&>th]:whitespace-nowrap [&>th]:px-3 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold [&>th]:text-slate-500">
                    <th>Owner / Broker</th><th className="text-right">Challans</th><th className="text-right">Total Hire</th><th className="text-right">Extra Charges</th><th className="text-right">Paid</th><th className="text-right">Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {ownerSummary.map((s) => (
                    <tr key={s.owner} className="border-t [&>td]:px-3 [&>td]:py-2" data-testid="owner-summary-row">
                      <td className="font-semibold">{s.owner}</td>
                      <td className="text-right tabular-nums">{s.challans}</td>
                      <td className="text-right tabular-nums">{inr(s.hire)}</td>
                      <td className="text-right tabular-nums text-orange-700" data-testid="owner-extra-charges">{s.extras ? inr(s.extras) : "—"}</td>
                      <td className="text-right tabular-nums text-emerald-700">{inr(s.paid)}</td>
                      <td className={`text-right font-bold tabular-nums ${s.pending > 0 ? "text-red-600" : "text-slate-500"}`}>{inr(s.pending)}</td>
                    </tr>
                  ))}
                  <tr className="border-t bg-slate-50 font-bold [&>td]:px-3 [&>td]:py-2">
                    <td>TOTAL</td>
                    <td className="text-right tabular-nums">{ownerSummary.reduce((a, s) => a + s.challans, 0)}</td>
                    <td className="text-right tabular-nums">{inr(ownerSummary.reduce((a, s) => a + s.hire, 0))}</td>
                    <td className="text-right tabular-nums text-orange-700">{inr(ownerSummary.reduce((a, s) => a + s.extras, 0))}</td>
                    <td className="text-right tabular-nums text-emerald-700">{inr(ownerSummary.reduce((a, s) => a + s.paid, 0))}</td>
                    <td className="text-right tabular-nums text-red-600">{inr(ownerSummary.reduce((a, s) => a + s.pending, 0))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )
        ) : entries === null ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No vehicle payments found for the selected owner/period.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-xs" data-testid="vehicle-payments-table">
              <thead className="bg-slate-50">
                <tr className="[&>th]:whitespace-nowrap [&>th]:px-3 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold [&>th]:text-slate-500">
                  <th>Date</th><th>Challan No.</th><th>Vehicle No.</th><th>Owner / Broker</th><th>Payment</th><th>Mode</th><th className="text-right">Unloading</th><th className="text-right">Challan Chg</th><th className="text-right">Extra Wt</th><th className="text-right">Extra Dly</th><th>Charges Reason</th><th className="text-right">Amount</th><th>By</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id} className="border-t [&>td]:px-3 [&>td]:py-2" data-testid="vehicle-payments-row">
                    <td className="whitespace-nowrap">{e.date ? fmtDate(e.date) : "—"}</td>
                    <td className="font-semibold">{String(e.challan_no || "—").replace(/^VHC[-\s]?/i, "")}</td>
                    <td className="whitespace-nowrap">{e.vehicle_no || "—"}</td>
                    <td>{e.owner}</td>
                    <td className="whitespace-nowrap">{String(e.type || "—").replace(/^Vehicle /, "")}</td>
                    <td>{e.mode || "—"}</td>
                    <td className="whitespace-nowrap text-right tabular-nums">{e.unloading ? inr(e.unloading) : "—"}</td>
                    <td className="whitespace-nowrap text-right tabular-nums">{e.challan_chg ? inr(e.challan_chg) : "—"}</td>
                    <td className="whitespace-nowrap text-right tabular-nums">{e.extra_wt ? inr(e.extra_wt) : "—"}</td>
                    <td className="whitespace-nowrap text-right tabular-nums">{e.extra_dly ? inr(e.extra_dly) : "—"}</td>
                    <td className="max-w-[160px] truncate text-[11px] text-slate-500" title={e.charge_reason || ""} data-testid="vp-charge-reason">{e.charge_reason || "—"}</td>
                    <td className="whitespace-nowrap text-right font-bold tabular-nums">{inr(e.amount)}</td>
                    <td>{e.by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChallanPaymentsDialog({ target, onClose, onChanged, isAdmin }) {
  const [payments, setPayments] = useState([]);
  const [ptype, setPtype] = useState("Vehicle Advance Payment");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(today());
  const [mode, setMode] = useState("Cash");
  const [remarks, setRemarks] = useState("");
  const [chequeNo, setChequeNo] = useState("");
  const [chequeDate, setChequeDate] = useState("");
  const [chequeAmount, setChequeAmount] = useState("");
  const [bankName, setBankName] = useState("");
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState({});
  const [editLog, setEditLog] = useState([]);
  useEffect(() => {
    if (!target) return;
    setPayments(target.payments || []);
    setPtype("Vehicle Advance Payment");
    setAmount(String(Number(target.advance_paid) || ""));
    setDate(today());
    setMode("Cash");
    setRemarks("");
    setChequeNo("");
    setChequeDate("");
    setChequeAmount("");
    setBankName("");
    setLive({
      unloading_charge: target.unloading_charge,
      challan_charge: target.challan_charge,
      extra_weight_charge: target.extra_weight_charge,
      extra_delivery_charge: target.extra_delivery_charge,
      balance: target.balance,
      extra_charge_reason: target.extra_charge_reason || "",
    });
    const chNo = String(target.challan_no || "").replace(/^VHC[-\s]?/i, "").trim();
    api.get("/change-requests")
      .then(({ data }) => setEditLog((data || []).filter((h) => h.field === "extra_charge_edit" && String(h.lr_no || "").replace(/^VHC[-\s]?/i, "").trim() === chNo).sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))))
      .catch(() => setEditLog([]));
  }, [target]);
  if (!target) return null;
  const addCharge = async (field, label) => {
    const cur = Number(live[field]) || 0;
    const v = window.prompt(`${label} — enter the amount (₹). This adds to the Balance paid to the vendor/owner.`, cur || "");
    if (v === null) return;
    const amt = Number(String(v).replace(/[,\s₹]/g, ""));
    if (isNaN(amt) || amt < 0) {
      window.alert("Enter a valid amount in rupees.");
      return;
    }
    let reason = "";
    if (amt > 0 && !String(live.extra_charge_reason || "").trim()) {
      reason = window.prompt("Reason for this extra charge (prints on the challan):") || "";
      if (!reason.trim()) {
        window.alert("A reason is required when an extra charge is added — nothing was saved.");
        return;
      }
    }
    try {
      const { data } = await api.post(`/hire-challans/${target.id}/extra-charge`, { field, amount: amt, reason });
      setLive((l) => ({ ...l, [field]: data[field], balance: data.balance, extra_charge_reason: data.extra_charge_reason || l.extra_charge_reason }));
      toast.success(`${label} set to ${inr(amt)} — balance is now ${inr(data.balance)}`);
      const by = "Admin";
      setEditLog((lg) => [{ id: `local-${Date.now()}`, created_at: new Date().toISOString(), field_label: `${label} (Vehicle Payments)`, old_value: inr(cur), new_value: `${inr(amt)} — ${reason || live.extra_charge_reason || ""}`, decided_by: by }, ...lg]);
      onChanged();
      const party = target.vendor_broker_name || target.owner_name || "the vendor";
      if (window.confirm(`WhatsApp ${party} the updated balance of ${inr(data.balance)}?`)) {
        const chNo = String(target.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—";
        const msg = [
          "*NTC — EXTRA AMOUNT ADDED* ₹",
          `Challan: ${chNo}`,
          `Vehicle: ${target.vehicle_no || "—"}`,
          `${label}: ${inr(amt)}`,
          (reason || live.extra_charge_reason) ? `Reason: ${reason || live.extra_charge_reason}` : "",
          "",
          `*New Balance Amount: ${inr(data.balance)}* (incl. extra charges)`,
          "This balance will be settled as per terms.",
        ].filter(Boolean).join("\n");
        const num = String(target.vendor_mobile || target.owner_mobile || "").replace(/\D/g, "");
        const wa = num ? (num.length === 10 ? "91" + num : num) : "";
        setTimeout(() => window.open(wa ? `https://wa.me/${wa}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank"), 150);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const prefillFor = (t) =>
    t === "Vehicle Hire Payment" ? Number(target.hire_amount) || "" : t === "Vehicle Advance Payment" ? Number(target.advance_paid) || "" : Number(target.balance) || "";
  const save = async () => {
    if (ptype === "Vehicle Balance Payment" && !target.pod_path) {
      const ok = window.confirm("⚠ No signed POD is attached to this challan.\n\nBalance payment should be released against the signed POD proof. Record the payment anyway?");
      if (!ok) return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/hire-challans/${target.id}/payments`, {
        type: ptype,
        amount: Number(amount) || 0,
        date,
        mode,
        remarks,
        ...(mode === "Cheque" ? { cheque_no: chequeNo, cheque_date: chequeDate, cheque_amount: Number(chequeAmount) || 0, bank_name: bankName } : {}),
      });
      setPayments((p) => [...p, res.data]);
      toast.success(`${ptype} of ${inr(Number(amount) || 0)} recorded`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const removePay = async (p) => {
    try {
      await api.delete(`/hire-challans/${target.id}/payments/${p.id}`);
      setPayments((list) => list.filter((x) => x.id !== p.id));
      toast.success("Payment entry removed");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="challan-payments-dialog">
        <DialogHeader>
          <DialogTitle>Vehicle Payments</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 text-center text-xs sm:grid-cols-4">
          <div className="rounded-md bg-sky-50 px-2 py-1.5" data-testid="challan-pay-challan-no"><div className="font-semibold text-slate-500">Challan No.</div><div className="font-bold">{String(target.challan_no || "—").replace(/^VHC[-\s]?/i, "")}</div></div>
          <div className="rounded-md bg-sky-50 px-2 py-1.5" data-testid="challan-pay-vehicle-no"><div className="font-semibold text-slate-500">Vehicle No.</div><div className="font-bold">{target.vehicle_no || "—"}</div></div>
          <div className="rounded-md bg-sky-50 px-2 py-1.5" data-testid="challan-pay-from"><div className="font-semibold text-slate-500">From</div><div className="font-bold">{target.origin || "—"}</div></div>
          <div className="rounded-md bg-sky-50 px-2 py-1.5" data-testid="challan-pay-destination"><div className="font-semibold text-slate-500">Destination</div><div className="font-bold">{target.destination || "—"}</div></div>
        </div>
        <div className="grid grid-cols-4 gap-2 text-center text-xs" data-testid="challan-pay-extra-charges">
          {[["unloading_charge", "Unloading Charge"], ["challan_charge", "Challan Charge"], ["extra_weight_charge", "Extra Weight Charge"], ["extra_delivery_charge", "Extra Delivery Charge"]].map(([k, label]) => (
            <div key={k} className="rounded-md bg-orange-50 px-2 py-1.5">
              <div className="font-semibold text-slate-500">{label}</div>
              <div className="font-bold tabular-nums text-orange-700">{Number(live[k]) ? inr(live[k]) : "—"}</div>
              {isAdmin && (
                <button
                  type="button"
                  className="mt-0.5 inline-flex items-center rounded-full border border-orange-300 bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-orange-700 hover:bg-orange-100"
                  title={`Add / change the ${label} — adds to the Balance Amount (admin only, recorded in the Correction Log Book)`}
                  onClick={() => addCharge(k, label)}
                  data-testid={`add-${k.replace(/_/g, "-")}-btn`}
                >
                  {Number(live[k]) ? "Edit" : "+ Add"}
                </button>
              )}
            </div>
          ))}
        </div>
        {editLog.length > 0 && (
          <div className="rounded-md border border-orange-200 bg-orange-50/50 px-3 py-2" data-testid="charge-edit-history">
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-orange-800">Charge Edit History ({editLog.length})</div>
            <div className="max-h-28 space-y-1 overflow-y-auto">
              {editLog.map((h) => (
                <div key={h.id} className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-slate-700" data-testid="charge-edit-history-row">
                  <span className="whitespace-nowrap text-slate-500">{fmtDate(String(h.created_at || "").slice(0, 10))}</span>
                  <span className="font-semibold">{String(h.field_label || "").replace(" (Vehicle Payments)", "")}</span>
                  <span className="text-slate-500">{h.old_value || "—"} →</span>
                  <span className="font-bold text-orange-700">{h.new_value || "—"}</span>
                  <span className="text-slate-500">by {h.decided_by || h.requested_by || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div className="rounded-md bg-slate-50 px-2 py-1.5"><div className="font-semibold text-slate-500">Vehicle Hire</div><div className="font-bold tabular-nums">{inr(target.hire_amount)}</div></div>
          <div className="rounded-md bg-slate-50 px-2 py-1.5"><div className="font-semibold text-slate-500">Advance Amount</div><div className="font-bold tabular-nums">{inr(target.advance_paid)}</div></div>
          <div className="rounded-md bg-slate-50 px-2 py-1.5"><div className="font-semibold text-slate-500">Balance Amount <span className="font-normal">(incl. extra charges)</span></div><div className="font-bold tabular-nums" data-testid="challan-pay-balance">{inr(live.balance)}</div></div>
        </div>
        {ptype === "Vehicle Balance Payment" && !target.pod_path && (
          <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="pod-missing-warning">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
            No signed POD attached to this challan — attach the POD (paperclip on the challan row) before releasing the balance payment.
          </div>
        )}
        {payments.length > 0 && (
          <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-xs" data-testid="challan-payments-table">
              <thead className="bg-slate-50">
                <tr className="[&>th]:px-2 [&>th]:py-1.5 [&>th]:text-left [&>th]:font-semibold [&>th]:text-slate-500">
                  <th>Payment</th><th>Date</th><th>Mode</th><th className="text-right">Amount</th><th>By</th><th></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t [&>td]:px-2 [&>td]:py-1.5" data-testid="challan-payment-row">
                    <td className="font-semibold">{p.type}</td>
                    <td className="whitespace-nowrap">{p.date ? fmtDate(p.date) : "—"}</td>
                    <td>{p.mode || "—"}{p.cheque_no ? ` · ${p.cheque_no}${p.bank_name ? ` (${p.bank_name})` : ""}` : ""}</td>
                    <td className="text-right font-bold tabular-nums">{inr(p.amount)}</td>
                    <td>{p.by || "—"}</td>
                    <td className="whitespace-nowrap text-right">
                      <button type="button" className="mr-2 text-primary hover:text-primary/70" onClick={() => printVehiclePayment(target, p)} title="Print payment voucher for owner's signature" data-testid="challan-payment-print-btn">
                        <Printer className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" className="text-red-500 hover:text-red-700" onClick={() => removePay(p)} title="Remove payment" data-testid="challan-payment-delete-btn">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="grid gap-3 rounded-md border border-emerald-200 bg-emerald-50/50 p-3 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label>Payment Type</Label>
            <Select value={ptype} onValueChange={(v) => { setPtype(v); setAmount(String(prefillFor(v))); }}>
              <SelectTrigger data-testid="challan-pay-type"><SelectValue /></SelectTrigger>
              <SelectContent>{PAY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Amount (₹)</Label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="challan-pay-amount" />
          </div>
          <div className="grid gap-1.5">
            <Label>Payment Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="challan-pay-date" />
          </div>
          <div className="grid gap-1.5">
            <Label>Payment Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="challan-pay-mode"><SelectValue /></SelectTrigger>
              <SelectContent>{PAY_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {mode === "Cheque" && (
            <div className="grid grid-cols-2 gap-3 rounded-md border border-sky-200 bg-sky-50/60 p-3 sm:col-span-2 sm:grid-cols-4" data-testid="challan-cheque-details">
              <div className="grid gap-1.5">
                <Label>Cheque No.</Label>
                <Input value={chequeNo} onChange={(e) => setChequeNo(e.target.value)} placeholder="e.g. 123456" data-testid="challan-cheque-no" />
              </div>
              <div className="grid gap-1.5">
                <Label>Cheque Date</Label>
                <Input type="date" value={chequeDate} onChange={(e) => setChequeDate(e.target.value)} data-testid="challan-cheque-date" />
              </div>
              <div className="grid gap-1.5">
                <Label>Cheque Amount</Label>
                <Input type="number" value={chequeAmount} onChange={(e) => setChequeAmount(e.target.value)} placeholder="0" data-testid="challan-cheque-amount" />
              </div>
              <div className="grid gap-1.5">
                <Label>Bank Name</Label>
                <Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. Punjab National Bank" data-testid="challan-bank-name" />
              </div>
            </div>
          )}
          <div className="grid gap-1.5 sm:col-span-2">
            <Label>Remarks</Label>
            <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="e.g. Paid to owner via UPI" data-testid="challan-pay-remarks" />
          </div>
          <div className="sm:col-span-2">
            <Button className="w-full" onClick={save} disabled={busy || !(Number(amount) > 0)} data-testid="challan-pay-save-btn">
              {busy ? "Saving…" : "Record Payment"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BlankChallanDialog({ rows, onClose, onPrint }) {
  const [startNo, setStartNo] = useState("");
  const [count, setCount] = useState(1);
  const [clashes, setClashes] = useState([]);
  const [warned, setWarned] = useState(false);
  useEffect(() => { if (rows) { setStartNo(""); setCount(1); setClashes([]); setWarned(false); } }, [rows]);
  const go = () => {
    const n = Math.min(Math.max(Number(count) || 1, 1), 50);
    const m = String(startNo).trim().match(/^(.*?)(\d+)$/);
    const list = Array.from({ length: n }, (_, i) => {
      if (!startNo.trim()) return { blank: true };
      if (!m || n === 1) return { blank: true, challan_no: startNo.trim() };
      return { blank: true, challan_no: m[1] + String(Number(m[2]) + i).padStart(m[2].length, "0") };
    });
    const norm = (v) => String(v || "").replace(/^VHC[-\s]?/i, "").trim().toLowerCase();
    const used = new Set((rows || []).map((r) => norm(r.challan_no)).filter(Boolean));
    const bad = list.map((l) => l.challan_no).filter((no) => no && used.has(norm(no)));
    if (bad.length && !warned) {
      setClashes(bad);
      setWarned(true);
      return;
    }
    onPrint(list);
    onClose();
  };
  const onChange = (fn) => (e) => { fn(e.target.value); setWarned(false); setClashes([]); };
  return (
    <Dialog open={!!rows} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[380px]" data-testid="blank-challan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Blank Challan Print</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Starting Challan No. (manual entry)</Label>
            <Input value={startNo} onChange={onChange(setStartNo)} placeholder="e.g. 4510 (leave blank for no number)" data-testid="blank-challan-start-no" />
          </div>
          <div className="grid gap-1.5">
            <Label>How many blank challans</Label>
            <Input type="number" min="1" max="50" value={count} onChange={onChange(setCount)} data-testid="blank-challan-count" />
            <p className="text-xs text-muted-foreground">Serial numbers auto-increment from the starting Challan No. so hand-filled challans stay traceable.</p>
          </div>
          {clashes.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="blank-challan-clash-warning">
              ⚠ Challan No. {clashes.join(", ")} already exist{clashes.length === 1 ? "s" : ""} in the system. Click "Print Anyway" to continue or change the number.
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant={clashes.length ? "destructive" : "default"} onClick={go} data-testid="blank-challan-continue-btn">
            <Printer className="mr-1.5 h-4 w-4" /> {clashes.length ? "Print Anyway" : "Choose Copies"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CopyChoiceDialog({ target, onClose, onPrinted }) {
  const [picked, setPicked] = useState(CHALLAN_COPIES);
  useEffect(() => { if (target) setPicked(CHALLAN_COPIES); }, [target]);
  const toggle = (cp) => setPicked((p) => (p.includes(cp) ? p.filter((x) => x !== cp) : [...p, cp]));
  const doPrint = () => {
    const copies = CHALLAN_COPIES.filter((c) => picked.includes(c));
    if (Array.isArray(target)) {
      printChallans(target, copies);
      if (target[0]?.blank) logPadIssue("Challan", target, "challan_no");
    } else {
      printChallan(target, copies);
      onPrinted?.(target);
    }
    onClose();
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[360px]" data-testid="copy-choice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Which copies to print?</DialogTitle>
        </DialogHeader>
        <div className="grid gap-1.5">
          {CHALLAN_COPIES.map((cp) => (
            <label key={cp} className={`flex cursor-pointer items-center gap-2.5 rounded-md border px-3 py-2 text-sm font-medium ${picked.includes(cp) ? "border-primary/40 bg-primary/5" : "border-border"}`} data-testid={`copy-option-${cp.split(" ")[0].toLowerCase()}`}>
              <input type="checkbox" className="h-4 w-4 accent-blue-600" checked={picked.includes(cp)} onChange={() => toggle(cp)} data-testid={`copy-check-${cp.split(" ")[0].toLowerCase()}`} />
              {cp.charAt(0) + cp.slice(1).toLowerCase()}
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={doPrint} disabled={picked.length === 0} data-testid="copy-choice-print-btn">
            <Printer className="mr-1.5 h-4 w-4" /> Print {picked.length} cop{picked.length === 1 ? "y" : "ies"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const MODES = ["Cash", "Bank", "UPI", "Cheque", "NEFT", "RTGS", "IMPS"];
const OFFICES = ["Chennai", "Tada", "Hyderabad", "Jaipur"];

const FY_FILTER_OPTIONS = (() => {
  const n = new Date();
  const curFy = n.getMonth() + 1 >= 4 ? n.getFullYear() : n.getFullYear() - 1;
  return ["All FY", ...Array.from({ length: 5 }, (_, i) => {
    const y = curFy - i;
    return `FY ${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  })];
})();

const isExpired = (d) => d && /^\d{4}-\d{2}-\d{2}$/.test(String(d)) && String(d) < today();

const csvCell = (v) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const exportChallansCsv = (list) => {
  const headers = ["Sl", "Branch", "Challan No.", "Loading Date", "Vehicle No.", "Vehicle Type", "Owner Name", "Owner Mobile No.", "Vendor/Broker Name", "From", "Destination", "C.N. No.", "Insurance Company", "Policy No.", "Insurance Valid Upto", "Vehicle Owner PAN No.", "Vendor/Broker PAN No.", "Vehicle Hire", "Unloading Charge", "Challan Charge", "Extra Weight Charge", "Extra Delivery Charge", "Advance Amount", "Balance Amount", "Advance Status", "Balance Status", "POD"];
  const lines = [headers.join(",")];
  (list || []).forEach((r, i) => {
    const hire = Number(r.hire_amount) || 0;
    const adv = Number(r.advance_paid) || 0;
    const bal = Number(r.balance ?? hire - adv) || 0;
    const types = new Set((r.payments || []).map((p) => p.type));
    lines.push([
      i + 1,
      r.engaged_office || r.office || "",
      String(r.challan_no || "").replace(/^VHC[-\s]?/i, ""),
      r.loading_date || r.date || "",
      r.vehicle_no || "",
      r.vehicle_type || "",
      r.owner_name || "",
      r.owner_mobile || "",
      r.vendor_broker_name || "",
      r.origin || "",
      r.destination || "",
      r.cn_no || "",
      r.insurance_co || "",
      r.policy_no || "",
      r.ins_valid_upto || "",
      r.pan_no || "",
      r.vendor_pan || "",
      hire,
      Number(r.unloading_charge) || 0,
      Number(r.challan_charge) || 0,
      Number(r.extra_weight_charge) || 0,
      Number(r.extra_delivery_charge) || 0,
      adv,
      bal,
      types.has("Vehicle Advance Payment") ? "Paid" : adv > 0 ? "Due" : "—",
      types.has("Vehicle Balance Payment") ? "Paid" : bal > 0 ? "Due" : "—",
      r.pod_path ? "Yes" : "No",
    ].map(csvCell).join(","));
  });
  const totHire = (list || []).reduce((s, r) => s + (Number(r.hire_amount) || 0), 0);
  const totAdv = (list || []).reduce((s, r) => s + (Number(r.advance_paid) || 0), 0);
  const totBal = (list || []).reduce((s, r) => s + (Number(r.balance ?? (Number(r.hire_amount) || 0) - (Number(r.advance_paid) || 0)) || 0), 0);
  const totUnl = (list || []).reduce((s, r) => s + (Number(r.unloading_charge) || 0), 0);
  const totChg = (list || []).reduce((s, r) => s + (Number(r.challan_charge) || 0), 0);
  const totWt = (list || []).reduce((s, r) => s + (Number(r.extra_weight_charge) || 0), 0);
  const totDly = (list || []).reduce((s, r) => s + (Number(r.extra_delivery_charge) || 0), 0);
  lines.push(["", "TOTAL", "", "", "", "", "", "", "", "", "", "", "", "", "", "", totHire, totUnl, totChg, totWt, totDly, totAdv, totBal, "", "", ""].map(csvCell).join(","));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `vehicle-hire-challans-${today()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

function ChargeApprovalsDialog({ rows, onClose, onDecided, focusId }) {
  const [fetched, setFetched] = useState(null);
  const [contacts, setContacts] = useState({});
  const [tab, setTab] = useState("pending");
  const [history, setHistory] = useState(null);
  const [histMonth, setHistMonth] = useState(today().slice(0, 7));
  const [histBranch, setHistBranch] = useState("All");
  const curFy = (() => { const d = new Date(); return d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1; })();
  const [fyYear, setFyYear] = useState(curFy);
  const [fyData, setFyData] = useState(null);
  const [fyPrev, setFyPrev] = useState(null);
  useEffect(() => {
    if (tab !== "fy") return;
    setFyData(null);
    setFyPrev(null);
    api.get(`/reports/yearly-charges?fy=${fyYear}`).then(({ data }) => setFyData(data)).catch(() => setFyData({ rows: [], totals: {} }));
    api.get(`/reports/yearly-charges?fy=${fyYear - 1}`).then(({ data }) => setFyPrev(data)).catch(() => setFyPrev(null));
  }, [tab, fyYear]);
  useEffect(() => {
    if (!rows) api.get("/hire-challans").then(({ data }) => setFetched(data || [])).catch(() => setFetched([]));
    api.get("/branch-contacts").then((r) => setContacts(r.data || {})).catch(() => {});
  }, [rows]);
  useEffect(() => {
    if (tab === "history" && history === null) {
      api.get("/change-requests")
        .then(({ data }) => setHistory((data || []).filter((x) => x.field === "charge_approval" || x.field === "charge_override" || x.field === "extra_charge_edit").sort((a, b) => String(b.decided_at || b.created_at || "").localeCompare(String(a.decided_at || a.created_at || "")))))
        .catch(() => setHistory([]));
    }
  }, [tab, history]);
  const source = rows || fetched || [];
  const exTot = (r) => (Number(r.unloading_charge) || 0) + (Number(r.challan_charge) || 0) + (Number(r.extra_weight_charge) || 0) + (Number(r.extra_delivery_charge) || 0);
  const ageDays = (r) => {
    const t = Date.parse(r.charge_submitted_at || r.created_at || r.date || "");
    return isNaN(t) ? 0 : Math.floor((Date.now() - t) / 86400000);
  };
  const pending = source.filter((r) => r.charge_approval_status === "Pending");
  const staleCount = pending.filter((r) => ageDays(r) > 2).length;
  const waStale = async () => {
    const stale = pending.filter((r) => ageDays(r) > 2);
    let mob = "";
    try {
      const r = await api.get("/settings/otp-mobile");
      mob = String(r.data?.mobile || "").replace(/\D/g, "");
    } catch {}
    const target = mob ? (mob.length === 10 ? "91" + mob : mob) : "";
    const msg = [
      "*NTC — STALE CHARGE APPROVALS* ⚠",
      `${stale.length} challan${stale.length === 1 ? "" : "s"} waiting over 2 days for extra-charge approval:`,
      "",
      ...stale.slice(0, 15).map((r) => `• Challan ${String(r.challan_no || "—").replace(/^VHC[-\s]?/i, "")} — ${r.vehicle_no || "—"} — ${inr(exTot(r))} — waiting ${ageDays(r)}d (${r.engaged_office || r.office || "—"})`),
      "",
      "Open Vehicle Hire Challan → Charge Approvals to decide.",
    ].join("\n");
    window.open(target ? `https://wa.me/${target}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const decide = async (r, action) => {
    let note = "";
    if (action === "reject") {
      note = window.prompt(
        `Rejecting the extra charges on challan ${String(r.challan_no || "").replace(/^VHC[-\s]?/i, "")} (${inr(exTot(r))}).\n\nWrite a short note for the submitting staff — why is it rejected?`
      );
      if (note === null) return;
      if (!String(note).trim()) {
        window.alert("A note is required when rejecting — the staff needs to know why. Nothing was rejected.");
        return;
      }
      note = String(note).trim();
    }
    try {
      await api.post(`/hire-challans/${r.id}/charge-approval`, { action, note });
      toast.success(`Extra charges ${action === "approve" ? "approved" : "rejected"} for challan ${String(r.challan_no || "").replace(/^VHC[-\s]?/i, "")}`);
      const branch = r.engaged_office || r.branch || r.office || "";
      if (window.confirm(`WhatsApp ${branch || "the branch"} that the extra charges were ${action === "approve" ? "approved" : "rejected"}?`)) {
        const chNo = String(r.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—";
        const msg = (action === "approve"
          ? [
              "*EXTRA CHARGES APPROVED* ✅",
              `Challan: ${chNo}`,
              `Vehicle: ${r.vehicle_no || "—"}`,
              `Extra charges: ${inr(exTot(r))}`,
              r.extra_charge_reason ? `Reason: ${r.extra_charge_reason}` : "",
              "",
              "Approved by admin — you may settle the balance including these charges.",
            ]
          : [
              "*EXTRA CHARGES REJECTED* ⛔",
              `Challan: ${chNo}`,
              `Vehicle: ${r.vehicle_no || "—"}`,
              `Extra charges: ${inr(exTot(r))}`,
              note ? `Admin's note: ${note}` : "",
              "",
              "Please edit the challan and correct its charges.",
            ]
        ).filter(Boolean).join("\n");
        const num = String(contacts[branch] || "").replace(/\D/g, "");
        const target = num ? (num.length === 10 ? "91" + num : num) : "";
        setTimeout(() => window.open(target ? `https://wa.me/${target}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank"), 150);
      }
      onDecided();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[820px]" data-testid="charge-approvals-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Charge Approvals — over-limit extra charges awaiting you</DialogTitle>
        </DialogHeader>
        {staleCount > 0 && (
          <div className="flex items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" data-testid="charge-stale-alert">
            <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 shrink-0" /> {staleCount} approval{staleCount > 1 ? "s have" : " has"} been waiting more than 2 days — decide these first so the queue never goes stale.</span>
            <Button size="sm" variant="outline" className="h-7 shrink-0 border-red-300 bg-white px-2.5 text-xs text-red-700 hover:bg-red-100" onClick={waStale} data-testid="stale-wa-btn">
              <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp Admin
            </Button>
          </div>
        )}
        <div className="flex gap-1.5">
          <button type="button" className={`rounded-md px-3 py-1.5 text-xs font-bold ${tab === "pending" ? "bg-orange-100 text-orange-800" : "bg-muted text-muted-foreground hover:bg-slate-200"}`} onClick={() => setTab("pending")} data-testid="charge-tab-pending">
            Pending ({pending.length})
          </button>
          <button type="button" className={`rounded-md px-3 py-1.5 text-xs font-bold ${tab === "history" ? "bg-orange-100 text-orange-800" : "bg-muted text-muted-foreground hover:bg-slate-200"}`} onClick={() => setTab("history")} data-testid="charge-tab-history">
            History
          </button>
          <button type="button" className={`rounded-md px-3 py-1.5 text-xs font-bold ${tab === "fy" ? "bg-orange-100 text-orange-800" : "bg-muted text-muted-foreground hover:bg-slate-200"}`} onClick={() => setTab("fy")} data-testid="charge-tab-fy">
            FY Summary
          </button>
          {tab === "fy" && (
            <span className="ml-auto flex items-center gap-1.5">
              <select className="h-8 rounded-md border px-2 text-xs" value={fyYear} onChange={(e) => setFyYear(Number(e.target.value))} data-testid="charge-fy-select">
                {Array.from({ length: 5 }, (_, i) => curFy - i).map((y) => (
                  <option key={y} value={y}>FY {y}-{String((y + 1) % 100).padStart(2, "0")}</option>
                ))}
              </select>
              <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!fyData} onClick={() => printYearlyCharges(fyData, fyPrev)} data-testid="charge-fy-print-btn">
                <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                disabled={!fyData}
                onClick={() => {
                  const list = (fyData.rows || []).filter((r) => r.charges_total > 0 || r.edits_count > 0);
                  const t = fyData.totals || {};
                  const lines = [["Branch", "Unloading Charge", "Challan Charge", "Extra Weight Charge", "Extra Delivery Charge", "Total Extra Charges", "Yearly Cap", "Over Cap", "Challans With Extras", "Admin Edits", "Edits Added"].join(",")];
                  [...list, ...(list.length ? [{ branch: "TOTAL", ...t }] : [])].forEach((r) => {
                    lines.push([r.branch, r.unloading_charge || 0, r.challan_charge || 0, r.extra_weight_charge || 0, r.extra_delivery_charge || 0, r.charges_total || 0, r.cap || "", r.over_cap ? "YES" : "", r.challans_with_extras || 0, r.edits_count || 0, r.edits_total || 0].join(","));
                  });
                  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
                  const u = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = u;
                  a.download = `yearly-extra-charges-FY${fyData.fy}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  setTimeout(() => URL.revokeObjectURL(u), 1000);
                  toast.success(`FY ${fyData.fy} summary exported (${list.length} branch${list.length === 1 ? "" : "es"})`);
                }}
                data-testid="charge-fy-excel-btn"
              >
                <Download className="mr-1.5 h-3.5 w-3.5" /> Excel
              </Button>
            </span>
          )}
          {tab === "history" && (
            <span className="ml-auto flex items-center gap-1.5">
              <input type="month" className="h-8 rounded-md border px-2 text-xs" value={histMonth} onChange={(e) => setHistMonth(e.target.value)} data-testid="charge-history-month" />
              <select className="h-8 rounded-md border px-2 text-xs" value={histBranch} onChange={(e) => setHistBranch(e.target.value)} data-testid="charge-history-branch">
                <option value="All">All Branches</option>
                {[...new Set((history || []).map((h) => h.office).filter(Boolean))].sort().map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => {
                  const inBranch = (h) => histBranch === "All" || (h.office || "") === histBranch;
                  const monthList = (history || []).filter((h) => String(h.decided_at || h.created_at || "").slice(0, 7) === histMonth && inBranch(h));
                  const list = monthList.filter((h) => h.field !== "extra_charge_edit");
                  const edits = monthList.filter((h) => h.field === "extra_charge_edit");
                  const label = /^\d{4}-\d{2}$/.test(histMonth) ? new Date(histMonth + "-01T00:00:00").toLocaleString("en-IN", { month: "long", year: "numeric" }) : histMonth;
                  printChargeDecisions(list, histBranch === "All" ? label : `${label} — ${histBranch} Branch`, edits);
                }}
                data-testid="charge-history-print-btn"
              >
                <Printer className="mr-1.5 h-3.5 w-3.5" /> Print Month
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => {
                  const list = (history || []).filter((h) => h.field !== "extra_charge_edit" && String(h.decided_at || h.created_at || "").slice(0, 7) === histMonth && (histBranch === "All" || (h.office || "") === histBranch));
                  const cellv = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
                  const lines = [["Decided On", "Challan No.", "Vehicle No.", "Branch", "Extra Charges", "Decision", "Note / Reason", "By"].join(",")];
                  list.forEach((h) => {
                    const rejected = /Rejected/i.test(h.field_label || "") || /^Rejected/i.test(h.new_value || "");
                    const note = String(h.new_value || "").replace(/^(Approved|Rejected)\s*—\s*/i, "").replace(/^Extra charges [^—]*—\s*/i, "");
                    const amt = (String(h.old_value || "").match(/₹[\d,]+/) || [""])[0] || (String(h.new_value || "").match(/₹[\d,]+/) || [""])[0];
                    lines.push([
                      String(h.decided_at || h.created_at || "").slice(0, 10),
                      h.lr_no || "",
                      h.customer_name || "",
                      h.office || "",
                      String(amt || "").replace(/[₹,]/g, ""),
                      rejected ? "Rejected" : "Approved",
                      note,
                      h.decided_by || h.requested_by || "",
                    ].map(cellv).join(","));
                  });
                  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
                  const u = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = u;
                  a.download = `charge-approval-decisions-${histMonth}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  setTimeout(() => URL.revokeObjectURL(u), 1000);
                  toast.success(`${list.length} decision(s) exported for ${histMonth}`);
                }}
                data-testid="charge-history-excel-btn"
              >
                <Download className="mr-1.5 h-3.5 w-3.5" /> Excel
              </Button>
            </span>
          )}
        </div>
        {tab === "pending" ? (
        <div className="overflow-x-auto rounded border">
          <table className="w-full text-xs">
            <thead className="bg-muted">
              <tr className="text-left [&>th]:p-2">
                <th>Challan No.</th><th>Date</th><th>Branch</th><th>Vehicle</th><th>Vendor / Owner</th><th className="text-right">Extra Charges</th><th>Reason</th><th></th>
              </tr>
            </thead>
            <tbody>
              {pending.map((r) => (
                <tr key={r.id} className={`border-t [&>td]:p-2 ${ageDays(r) > 2 ? "bg-red-50" : focusId === r.id ? "bg-amber-50" : ""}`} data-testid="charge-approval-row">
                  <td className="font-semibold">
                    {String(r.challan_no || "—").replace(/^VHC[-\s]?/i, "")}
                    {Number(r.charge_resubmit_count) > 0 && (
                      <span className="ml-1.5 inline-flex items-center rounded-full bg-purple-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-purple-700" title={`These charges were rejected and resubmitted ${r.charge_resubmit_count} time(s)`} data-testid="resubmit-count-pill">
                        resubmitted ×{r.charge_resubmit_count}
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap">
                    {fmtDate(r.loading_date || r.date)}
                    {ageDays(r) > 2 && (
                      <span className="ml-1.5 inline-flex items-center rounded-full bg-red-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-red-700" title={`In the queue for ${ageDays(r)} days`} data-testid="charge-age-pill">
                        waiting {ageDays(r)}d
                      </span>
                    )}
                  </td>
                  <td>{r.branch || r.office || "—"}</td>
                  <td className="whitespace-nowrap">{r.vehicle_no || "—"}</td>
                  <td>{r.vendor_broker_name || r.owner_name || "—"}</td>
                  <td className="text-right font-bold tabular-nums text-orange-700">{inr(exTot(r))}</td>
                  <td className="max-w-[150px] text-[11px] text-slate-600">{r.extra_charge_reason || "—"}</td>
                  <td className="whitespace-nowrap text-right">
                    <Button size="sm" className="mr-1 h-7 bg-emerald-600 px-2.5 text-xs text-white hover:bg-emerald-700" onClick={() => decide(r, "approve")} data-testid="charge-approve-btn">Approve</Button>
                    <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs text-red-600" onClick={() => decide(r, "reject")} data-testid="charge-reject-btn">Reject</Button>
                  </td>
                </tr>
              ))}
              {pending.length === 0 && (
                <tr><td colSpan={8} className="p-4 text-center text-muted-foreground">No extra charges waiting for approval. 🎉</td></tr>
              )}
            </tbody>
          </table>
        </div>
        ) : tab === "history" ? (
        <div className="overflow-x-auto rounded border">
          <table className="w-full text-xs">
            <thead className="bg-muted">
              <tr className="text-left [&>th]:p-2">
                <th>Decided On</th><th>Challan No.</th><th>Vehicle</th><th>Decision</th><th>Note / Reason</th><th>By</th>
              </tr>
            </thead>
            <tbody>
              {(history || []).filter((h) => h.field !== "extra_charge_edit").slice(0, 40).map((h) => {
                const rejected = /Rejected/i.test(h.field_label || "") || /^Rejected/i.test(h.new_value || "");
                const note = String(h.new_value || "").replace(/^(Approved|Rejected)\s*—\s*/i, "").replace(/^Extra charges [^—]*—\s*/i, "");
                return (
                  <tr key={h.id} className="border-t [&>td]:p-2" data-testid="charge-history-row">
                    <td className="whitespace-nowrap">{fmtDate(String(h.decided_at || h.created_at || "").slice(0, 10))}</td>
                    <td className="font-semibold">{h.lr_no || "—"}</td>
                    <td className="whitespace-nowrap">{h.customer_name || "—"}</td>
                    <td>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${rejected ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {rejected ? "Rejected" : "Approved"}
                      </span>
                      {h.field === "charge_override" && <span className="ml-1 text-[10px] text-slate-500">(over-limit save)</span>}
                    </td>
                    <td className="max-w-[220px] text-[11px] text-slate-600">{note || "—"}</td>
                    <td className="whitespace-nowrap">{h.decided_by || h.requested_by || "—"}</td>
                  </tr>
                );
              })}
              {history !== null && history.filter((h) => h.field !== "extra_charge_edit").length === 0 && (
                <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">No approve/reject decisions recorded yet.</td></tr>
              )}
              {history === null && (
                <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">Loading history…</td></tr>
              )}
            </tbody>
          </table>
        </div>
        ) : (
        <div className="overflow-x-auto rounded border" data-testid="charge-fy-table">
          {fyData === null ? (
            <div className="p-4 text-center text-xs text-muted-foreground">Loading FY summary…</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr className="text-left [&>th]:p-2">
                  <th>Branch</th><th className="text-right">Unloading</th><th className="text-right">Challan</th><th className="text-right">Extra Wt.</th><th className="text-right">Extra Dly.</th><th className="text-right">Total Charges</th><th className="text-right">Yearly Cap</th><th className="text-right">Challans</th><th className="text-right">Admin Edits</th><th className="text-right">Edits Added</th>
                </tr>
              </thead>
              <tbody>
                {(fyData.rows || []).filter((r) => r.charges_total > 0 || r.edits_count > 0).map((r) => (
                  <tr key={r.branch} className={`border-t [&>td]:p-2 ${r.over_cap ? "bg-red-50" : ""}`} data-testid="charge-fy-row">
                    <td className="font-semibold">
                      {r.branch}
                      {r.over_cap && (
                        <span className="ml-1.5 inline-flex items-center rounded-full bg-red-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-red-700" title={`This branch crossed its yearly extra-charges cap of ${inr(r.cap)}`} data-testid="fy-over-cap-pill">
                          over cap
                        </span>
                      )}
                    </td>
                    <td className="text-right tabular-nums">{Number(r.unloading_charge) ? inr(r.unloading_charge) : "—"}</td>
                    <td className="text-right tabular-nums">{Number(r.challan_charge) ? inr(r.challan_charge) : "—"}</td>
                    <td className="text-right tabular-nums">{Number(r.extra_weight_charge) ? inr(r.extra_weight_charge) : "—"}</td>
                    <td className="text-right tabular-nums">{Number(r.extra_delivery_charge) ? inr(r.extra_delivery_charge) : "—"}</td>
                    <td className={`text-right font-bold tabular-nums ${r.over_cap ? "text-red-600" : "text-orange-700"}`}>{Number(r.charges_total) ? inr(r.charges_total) : "—"}</td>
                    <td className="text-right tabular-nums">{Number(r.cap) ? inr(r.cap) : "—"}</td>
                    <td className="text-right tabular-nums">{r.challans_with_extras || "—"}</td>
                    <td className="text-right tabular-nums">{r.edits_count || "—"}</td>
                    <td className="text-right tabular-nums">{Number(r.edits_total) ? inr(r.edits_total) : "—"}</td>
                  </tr>
                ))}
                {(fyData.rows || []).filter((r) => r.charges_total > 0 || r.edits_count > 0).length === 0 && (
                  <tr><td colSpan={10} className="p-4 text-center text-muted-foreground">No extra charges or admin edits in FY {fyData.fy}.</td></tr>
                )}
                {(fyData.rows || []).some((r) => r.charges_total > 0 || r.edits_count > 0) && (
                  <tr className="border-t bg-slate-50 font-bold [&>td]:p-2" data-testid="charge-fy-totals">
                    <td>TOTAL</td>
                    <td className="text-right tabular-nums">{Number(fyData.totals?.unloading_charge) ? inr(fyData.totals.unloading_charge) : "—"}</td>
                    <td className="text-right tabular-nums">{Number(fyData.totals?.challan_charge) ? inr(fyData.totals.challan_charge) : "—"}</td>
                    <td className="text-right tabular-nums">{Number(fyData.totals?.extra_weight_charge) ? inr(fyData.totals.extra_weight_charge) : "—"}</td>
                    <td className="text-right tabular-nums">{Number(fyData.totals?.extra_delivery_charge) ? inr(fyData.totals.extra_delivery_charge) : "—"}</td>
                    <td className="text-right tabular-nums text-orange-700">
                      {Number(fyData.totals?.charges_total) ? inr(fyData.totals.charges_total) : "—"}
                      {fyPrev && Number(fyPrev.totals?.charges_total) > 0 && Number(fyData.totals?.charges_total) > 0 && (() => {
                        const pct = Math.round(((fyData.totals.charges_total - fyPrev.totals.charges_total) / fyPrev.totals.charges_total) * 100);
                        return (
                          <span className={`ml-1 text-[10px] font-bold ${pct > 0 ? "text-red-600" : "text-emerald-600"}`} data-testid="fy-delta">
                            {pct > 0 ? "▲" : "▼"} {Math.abs(pct)}%
                          </span>
                        );
                      })()}
                    </td>
                    <td></td>
                    <td className="text-right tabular-nums">{fyData.totals?.challans_with_extras || "—"}</td>
                    <td className="text-right tabular-nums">{fyData.totals?.edits_count || "—"}</td>
                    <td className="text-right tabular-nums">{Number(fyData.totals?.edits_total) ? inr(fyData.totals.edits_total) : "—"}</td>
                  </tr>
                )}
                {fyPrev && (fyPrev.rows || []).some((r) => r.charges_total > 0 || r.edits_count > 0) && (
                  <tr className="border-t text-slate-500 [&>td]:p-2" data-testid="charge-fy-prev-row">
                    <td className="font-semibold">LAST FY ({fyPrev.fy})</td>
                    <td className="text-right tabular-nums">{Number(fyPrev.totals?.unloading_charge) ? inr(fyPrev.totals.unloading_charge) : "—"}</td>
                    <td className="text-right tabular-nums">{Number(fyPrev.totals?.challan_charge) ? inr(fyPrev.totals.challan_charge) : "—"}</td>
                    <td className="text-right tabular-nums">{Number(fyPrev.totals?.extra_weight_charge) ? inr(fyPrev.totals.extra_weight_charge) : "—"}</td>
                    <td className="text-right tabular-nums">{Number(fyPrev.totals?.extra_delivery_charge) ? inr(fyPrev.totals.extra_delivery_charge) : "—"}</td>
                    <td className="text-right font-semibold tabular-nums">{Number(fyPrev.totals?.charges_total) ? inr(fyPrev.totals.charges_total) : "—"}</td>
                    <td></td>
                    <td className="text-right tabular-nums">{fyPrev.totals?.challans_with_extras || "—"}</td>
                    <td className="text-right tabular-nums">{fyPrev.totals?.edits_count || "—"}</td>
                    <td className="text-right tabular-nums">{Number(fyPrev.totals?.edits_total) ? inr(fyPrev.totals.edits_total) : "—"}</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        )}
        <p className="text-xs text-muted-foreground">Every decision is recorded in the Correction Log Book. Rejected challans stay saved — edit the challan to correct its charges.</p>
      </DialogContent>
    </Dialog>
  );
}

function ExtraChargesReportDialog({ rows, onClose }) {
  const [month, setMonth] = useState(today().slice(0, 7));
  const exTot = (r) => (Number(r.unloading_charge) || 0) + (Number(r.challan_charge) || 0) + (Number(r.extra_weight_charge) || 0) + (Number(r.extra_delivery_charge) || 0);
  const list = (rows || []).filter((r) => String(r.loading_date || r.date || "").slice(0, 7) === month && exTot(r) > 0);
  const total = list.reduce((s, r) => s + exTot(r), 0);
  const label = /^\d{4}-\d{2}$/.test(month) ? new Date(month + "-01T00:00:00").toLocaleString("en-IN", { month: "long", year: "numeric" }) : month;
  const downloadCsv = () => {
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const lines = [["Branch", "Loading Date", "Challan No.", "Vehicle No.", "Vendor / Owner", "Unloading Charge", "Challan Charge", "Extra Weight Charge", "Extra Delivery Charge", "Total", "Reason"].join(",")];
    const byBranch = {};
    list.forEach((r) => { const b = r.engaged_office || r.office || "Chennai"; (byBranch[b] = byBranch[b] || []).push(r); });
    Object.keys(byBranch).sort().forEach((b) => {
      let sub = 0;
      byBranch[b].forEach((r) => {
        sub += exTot(r);
        lines.push([b, r.loading_date || "", String(r.challan_no || "").replace(/^VHC[-\s]?/i, ""), r.vehicle_no || "", r.vendor_broker_name || r.owner_name || "", Number(r.unloading_charge) || 0, Number(r.challan_charge) || 0, Number(r.extra_weight_charge) || 0, Number(r.extra_delivery_charge) || 0, exTot(r), r.extra_charge_reason || ""].map(cell).join(","));
      });
      lines.push([`${b} TOTAL`, "", "", "", "", "", "", "", "", sub, ""].map(cell).join(","));
    });
    lines.push(["GRAND TOTAL", "", "", "", "", "", "", "", "", total, ""].map(cell).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `extra-charges-${month}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[420px]" data-testid="extra-charges-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Extra Charges Report</DialogTitle>
        </DialogHeader>
        <div className="grid gap-1.5">
          <Label>Month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="charges-report-month" />
        </div>
        <p className="text-xs text-muted-foreground" data-testid="charges-report-summary">
          {list.length} challan(s) with extra charges in {label} · Total {inr(total)}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={downloadCsv} disabled={!list.length} data-testid="charges-report-excel-btn">
            Excel ↓
          </Button>
          <Button className="flex-1" onClick={() => printExtraChargesReport(list, label)} disabled={!list.length} data-testid="charges-report-print-btn">
            <Printer className="mr-2 h-4 w-4" /> Print Branch-wise Sheet
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PodPendingDialog({ open, onClose, onAttach }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!open) return;
    setItems(null);
    Promise.all([api.get("/hire-challans"), api.get("/shipments")])
      .then(([c, s]) => {
        const byId = {};
        const byCn = {};
        (s.data || []).forEach((x) => {
          byId[x.id] = x;
          if (x.consignment_no) byCn[x.consignment_no] = x;
        });
        const flagged = (c.data || []).filter((r) => {
          if (r.pod_path) return false;
          const ship = byId[r.consignment_id] || byCn[r.cn_no] || byCn[r.consignment_no];
          return ship?.status === "Delivered";
        });
        flagged.sort((a, b) => (String(a.loading_date || "") < String(b.loading_date || "") ? -1 : 1));
        setItems(flagged);
      })
      .catch(() => setItems([]));
  }, [open]);
  const share = () => {
    const msg = [
      "*NTC — POD Pending List*",
      "Delivered trips with no signed POD attached:",
      ...(items || []).slice(0, 15).map((r, i) => `${i + 1}. Challan ${String(r.challan_no || "—").replace(/^VHC[-\s]?/i, "")} — ${fmtDate(r.loading_date || r.date)} — ${r.vehicle_no || "—"} — ${r.vendor_broker_name || r.owner_name || "—"} — ${r.origin || "?"}→${r.destination || "?"} — Balance ${inr(r.balance)}`),
      ...((items || []).length > 15 ? [`…and ${items.length - 15} more`] : []),
      "",
      "Please collect & upload the signed PODs before releasing balance payments.",
    ].join("\n");
    window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="pod-pending-dialog">
        <DialogHeader>
          <DialogTitle>POD Pending — delivered trips without POD</DialogTitle>
        </DialogHeader>
        {items === null ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground" data-testid="pod-pending-empty">All delivered trips have PODs attached. 🎉</div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-amber-700">{items.length} challan(s) need a POD before balance release</span>
              <Button size="sm" className="ml-auto h-8 bg-emerald-600 text-white hover:bg-emerald-700" onClick={share} data-testid="pod-pending-wa-btn">
                <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
              </Button>
            </div>
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-left [&>tr>th]:px-2.5 [&>tr>th]:py-2">
                  <tr><th>Challan No.</th><th>Loading Date</th><th>Vehicle No.</th><th>Vendor / Owner</th><th>Route</th><th className="text-right">Balance</th><th></th></tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} className="border-t [&>td]:px-2.5 [&>td]:py-2" data-testid="pod-pending-row">
                      <td className="font-bold">{String(r.challan_no || "—").replace(/^VHC[-\s]?/i, "")}</td>
                      <td>{fmtDate(r.loading_date || r.date)}</td>
                      <td>{r.vehicle_no || "—"}</td>
                      <td>{r.vendor_broker_name || r.owner_name || "—"}</td>
                      <td>{r.origin || "—"} → {r.destination || "—"}</td>
                      <td className="text-right font-bold tabular-nums">{inr(r.balance)}</td>
                      <td>
                        <PodCell row={r} onChanged={() => { onAttach(); onClose(); }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function VehicleHireChallan() {
  const [vendors, setVendors] = useState([]);
  const [fleetVehicles, setFleetVehicles] = useState([]);
  const [marketAll, setMarketAll] = useState([]);
  const [podPendOpen, setPodPendOpen] = useState(false);
  const [reminded, setReminded] = useState({});
  const [remRegOpen, setRemRegOpen] = useState(false);
  const [remMonth, setRemMonth] = useState(() => today().slice(0, 7));
  const [remRows, setRemRows] = useState([]);
  useEffect(() => {
    if (!remRegOpen) return;
    api.get("/hire-challans").then((r) => setRemRows((r.data || []).filter((c) => c.ins_reminder_at))).catch(() => {});
  }, [remRegOpen]);
  const [dueRegOpen, setDueRegOpen] = useState(false);
  const [dueMonth, setDueMonth] = useState(() => today().slice(0, 7));
  const [dueRows, setDueRows] = useState([]);
  useEffect(() => {
    if (!dueRegOpen) return;
    api.get("/hire-challans").then((r) => setDueRows(r.data || [])).catch(() => {});
  }, [dueRegOpen]);
  const duePaidOf = (c) => (c.payments || []).filter((p) => p.type === "Vehicle Balance Payment").reduce((a, p) => a + (Number(p.amount) || 0), 0);
  const dueAmtOf = (c) => Math.max((Number(c.balance) || 0) - duePaidOf(c), 0);
  const dueList = dueRows.filter((c) => !c.settled && dueAmtOf(c) > 0 && String(c.date || c.loading_date || "").startsWith(dueMonth));
  const [dueView, setDueView] = useState("due");
  const settledList = dueRows.filter((c) => c.settled && String(c.settled_at || "").startsWith(dueMonth));
  const [ackRegOpen, setAckRegOpen] = useState(false);
  const [ackMonth, setAckMonth] = useState(() => today().slice(0, 7));
  const [ackRows, setAckRows] = useState([]);
  useEffect(() => {
    if (!ackRegOpen) return;
    api.get("/hire-challans").then((r) => setAckRows(r.data || [])).catch(() => {});
  }, [ackRegOpen]);
  const ackList = ackRows.filter((c) => String(c.date || c.loading_date || "").startsWith(ackMonth));
  const [ackView, setAckView] = useState("month");
  const [oldVehOpen, setOldVehOpen] = useState(false);
  const [oldVehRows, setOldVehRows] = useState([]);
  useEffect(() => {
    if (!oldVehOpen) return;
    api.get("/hire-challans").then((r) => setOldVehRows(r.data || [])).catch(() => {});
  }, [oldVehOpen]);
  const makeYearOf = (c) => parseInt(String(c.make || "").match(/(19|20)\d{2}/)?.[0] || "", 10) || 0;
  const oldVehList = (() => {
    const seen = {};
    [...oldVehRows]
      .sort((a, b) => String(b.loading_date || b.date || "").localeCompare(String(a.loading_date || a.date || "")))
      .forEach((c) => {
        const y = makeYearOf(c);
        if (!y || new Date().getFullYear() - y <= 15) return;
        const k = String(c.vehicle_no || "").replace(/\s+/g, "").toUpperCase();
        if (!k || seen[k]) return;
        seen[k] = c;
      });
    return Object.values(seen).sort((a, b) => makeYearOf(a) - makeYearOf(b));
  })();
  const [docExpOpen, setDocExpOpen] = useState(false);
  const [routeRegOpen, setRouteRegOpen] = useState(false);
  const [chargesRep, setChargesRep] = useState(null);
  const [chargeApprovals, setChargeApprovals] = useState(null);
  const [reasonPicks, setReasonPicks] = useState(["Night unloading", "Overweight", "Border challan", "Toll / permit fee", "Loading delay halting"]);
  const [chargeLimit, setChargeLimit] = useState(0);
  const [chargeBranchLims, setChargeBranchLims] = useState({});
  useEffect(() => {
    api.get("/settings/charge-reasons").then((r) => {
      if (Array.isArray(r.data?.reasons) && r.data.reasons.length) setReasonPicks(r.data.reasons);
      setChargeLimit(Number(r.data?.limit) || 0);
      setChargeBranchLims(r.data?.branches || {});
    }).catch(() => {});
  }, []);
  const [routeRegRows, setRouteRegRows] = useState([]);
  const [routeRegFm, setRouteRegFm] = useState([]);
  useEffect(() => {
    if (!routeRegOpen) return;
    api.get("/hire-challans").then((r) => setRouteRegRows(r.data || [])).catch(() => {});
    api.get("/freight-master").then((r) => setRouteRegFm(r.data || [])).catch(() => {});
  }, [routeRegOpen]);
  const routeStats = (() => {
    const nm = (s) => String(s || "").trim().toLowerCase();
    const curM = today().slice(0, 7);
    const pd = new Date(curM + "-01T00:00:00");
    pd.setMonth(pd.getMonth() - 1);
    const prevM = pd.toISOString().slice(0, 7);
    const inM = (c, mm) => String(c.loading_date || c.date || "").slice(0, 7) === mm;
    const avgOf = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
    const g = {};
    routeRegRows.forEach((c) => {
      if (!c.origin || !c.destination || !(Number(c.hire_amount) > 0)) return;
      const k = nm(c.origin) + "|" + nm(c.destination);
      (g[k] = g[k] || { origin: c.origin, destination: c.destination, hires: [] }).hires.push(c);
    });
    return Object.values(g).map((r) => {
      const sorted = [...r.hires].sort((a, b) => String(b.loading_date || b.date || "").localeCompare(String(a.loading_date || a.date || "")));
      const amts = r.hires.map((c) => Number(c.hire_amount) || 0);
      const curAvg = avgOf(r.hires.filter((c) => inM(c, curM)).map((c) => Number(c.hire_amount) || 0));
      const prevAvg = avgOf(r.hires.filter((c) => inM(c, prevM)).map((c) => Number(c.hire_amount) || 0));
      const fm = routeRegFm.find((f) => nm(f.origin) === nm(r.origin) && nm(f.destination) === nm(r.destination) && Number(f.freight_amount) > 0);
      return {
        origin: r.origin,
        destination: r.destination,
        trips: r.hires.length,
        avg: avgOf(amts),
        min: Math.min(...amts),
        max: Math.max(...amts),
        last: sorted[0],
        saved: fm ? Number(fm.freight_amount) : 0,
        trend: curAvg && prevAvg ? Math.round(curAvg - prevAvg) : null,
      };
    }).sort((a, b) => b.trips - a.trips);
  })();
  const [docExpRows, setDocExpRows] = useState([]);
  const [docExpVault, setDocExpVault] = useState([]);
  useEffect(() => {
    if (!docExpOpen) return;
    api.get("/hire-challans").then((r) => setDocExpRows(r.data || [])).catch(() => {});
    api.get("/vehicle-docs-all").then((r) => setDocExpVault(r.data || [])).catch(() => {});
  }, [docExpOpen]);
  const lapsedDocsOf = (c) => {
    const docs = [["Insurance", c.ins_valid_upto], ["Permit", c.permit_valid_upto], ["FC", c.fc_valid_upto], ["DL", c.dl_valid_upto || c.dl_validity]];
    return docs.filter(([, d]) => isExpired(String(d || "").slice(0, 10)));
  };
  const soonDocsOf = (c) => {
    const t = today();
    const docs = [["Insurance", c.ins_valid_upto], ["Permit", c.permit_valid_upto], ["FC", c.fc_valid_upto], ["DL", c.dl_valid_upto || c.dl_validity]];
    return docs.filter(([, d]) => {
      const s = String(d || "").slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(s) || s < t) return false;
      return (new Date(s) - new Date(t)) / 86400000 <= 30;
    });
  };
  const docExpGroups = (() => {
    const seen = {};
    [...docExpRows]
      .sort((a, b) => String(b.loading_date || b.date || "").localeCompare(String(a.loading_date || a.date || "")))
      .forEach((c) => {
        const k = String(c.vehicle_no || "").replace(/\s+/g, "").toUpperCase();
        if (!k || seen[k]) return;
        seen[k] = { ...c };
      });
    const vaultByVeh = {};
    docExpVault.forEach((d) => {
      if (d.valid_upto) (vaultByVeh[d.vehicle_key] = vaultByVeh[d.vehicle_key] || {})[d.doc_type] = d;
    });
    Object.entries(vaultByVeh).forEach(([k, docs]) => {
      const c = seen[k] || (seen[k] = { vehicle_no: (Object.values(docs)[0] || {}).vehicle_no || k, owner_name: "", owner_mobile: "" });
      if (docs.Insurance) c.ins_valid_upto = docs.Insurance.valid_upto;
      if (docs.Permit) c.permit_valid_upto = docs.Permit.valid_upto;
      if (docs.FC) c.fc_valid_upto = docs.FC.valid_upto;
      if (docs.DL) c.dl_valid_upto = docs.DL.valid_upto;
    });
    const g = {};
    Object.values(seen).forEach((c) => {
      const lapsed = lapsedDocsOf(c);
      const soon = soonDocsOf(c);
      if (!lapsed.length && !soon.length) return;
      const owner = (c.owner_name || c.vendor_broker_name || "Vehicle Owner").trim();
      const mobile = String(c.owner_mobile || c.vendor_mobile || "").replace(/\D/g, "");
      const gk = (owner + "|" + mobile).toLowerCase();
      g[gk] = g[gk] || { owner, mobile, vehicles: [] };
      g[gk].vehicles.push({ vehicle_no: c.vehicle_no, lapsed, soon });
    });
    return Object.values(g).sort((a, b) => b.vehicles.length - a.vehicles.length);
  })();
  const docExpWa = (grp) => {
    const docLine = (list, verb) => list.map(([n, d]) => `${n} (${verb} ${fmtDate(String(d).slice(0, 10))})`).join(", ");
    const expLines = grp.vehicles.filter((v) => v.lapsed.length).map((v) => `• ${v.vehicle_no || "—"} — ${docLine(v.lapsed, "expired")}`);
    const soonLines = grp.vehicles.filter((v) => v.soon.length).map((v) => `• ${v.vehicle_no || "—"} — ${docLine(v.soon, "expires")}`);
    const msg = [
      "⚠ *NTC — Vehicle Document Renewal Reminder*",
      `Dear ${grp.owner},`,
      ...(expLines.length ? ["", "The following documents of your vehicle(s) engaged with us have EXPIRED:", ...expLines] : []),
      ...(soonLines.length ? ["", "Expiring within 30 days — renew in time:", ...soonLines] : []),
      "",
      "Please renew immediately and share the updated copies.",
      "— Nisha Transport Corporation",
    ].join("\n");
    const target = grp.mobile ? (grp.mobile.length === 10 ? "91" + grp.mobile : grp.mobile) : "";
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const docExpWaAll = () => {
    const docLine = (list, verb) => list.map(([n, d]) => `${n} (${verb} ${fmtDate(String(d).slice(0, 10))})`).join(", ");
    const msg = [
      "⚠ *NTC — Vehicle Documents Expired / Expiring Soon*",
      `${docExpGroups.reduce((a, g) => a + g.vehicles.length, 0)} vehicle(s) with lapsed or soon-expiring Insurance / Permit / FC:`,
      "",
      ...docExpGroups.flatMap((grp) =>
        grp.vehicles.map((v) => `• ${v.vehicle_no || "—"} — ${[docLine(v.lapsed, "expired"), docLine(v.soon, "expires")].filter(Boolean).join(", ")} — ${grp.owner}${grp.mobile ? ` (${grp.mobile})` : ""}`)
      ),
      "",
      "Chase the owners for renewals.",
    ].join("\n");
    const digits = String(localStorage.getItem("ntc_company_mobile") || "").replace(/\D/g, "");
    const target = digits ? (digits.length === 10 ? "91" + digits : digits) : "";
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const VAULT_TYPES = ["RC", "Insurance", "Permit", "FC", "DL", "PUC"];
  const vendorScore = (() => {
    const m = {};
    ackRows.forEach((c) => {
      const v = (c.vendor_broker_name || c.owner_name || "").trim();
      if (!v) return;
      const k = v.toLowerCase();
      m[k] = m[k] || { name: v, total: 0, back: 0, out: 0, sumDays: 0 };
      m[k].total++;
      const ld = String(c.loading_date || c.date || "").slice(0, 10);
      if (c.pod_ack_received) {
        m[k].back++;
        if (c.pod_ack_date && /^\d{4}-\d{2}-\d{2}$/.test(ld)) m[k].sumDays += Math.max(Math.floor((new Date(c.pod_ack_date) - new Date(ld)) / 86400000), 0);
      } else {
        m[k].out++;
      }
    });
    return Object.values(m)
      .map((x) => ({ ...x, avg: x.back ? Math.round((x.sumDays / x.back) * 10) / 10 : null }))
      .sort((a, b) => (a.avg ?? 1e9) - (b.avg ?? 1e9) || a.out - b.out);
  })();
  const settleChallan = (c) => {
    if (!c.settled && !c.pod_ack_received) {
      const no = String(c.challan_no || "").replace(/^VHC[-\s]?/i, "");
      const ok = window.confirm(`⚠ Signed POD for challan ${no} has NOT come back from the driver yet.\n\nBalance is normally held until the signed POD is received back.\n\nSettle the balance anyway?`);
      if (!ok) return;
    }
    api.post(`/hire-challans/${c.id}/settle`)
      .then((r) => {
        setDueRows((rows2) =>
          rows2.map((x) =>
            x.id === c.id
              ? {
                  ...x,
                  settled: r.data.settled,
                  settled_at: r.data.settled_at || "",
                  settled_by: r.data.settled_by || "",
                  payments: r.data.settled
                    ? [...(x.payments || []), ...(r.data.payment ? [r.data.payment] : [])]
                    : (x.payments || []).filter((p) => !p.settlement),
                }
              : x
          )
        );
        setReloadKey((k) => k + 1);
        if (r.data.settled) {
          const amt = r.data.payment?.amount ?? dueAmtOf(c);
          const settleWa = () => {
            const no = String(c.challan_no || "").replace(/^VHC[-\s]?/i, "");
            const msg = [
              "*NTC — Settlement Voucher*",
              `Challan No.: ${no}`,
              `Vehicle: ${c.vehicle_no || "—"}`,
              `Vendor/Owner: ${c.vendor_broker_name || c.owner_name || "—"}`,
              `Vehicle Hire: ${inr(c.hire_amount)} | Advance: ${inr(c.advance_paid)}`,
              `Settled Amount: *${inr(amt)}* (Cash)`,
              `Settled On: ${fmtDate(String(r.data.settled_at || "").slice(0, 10))} by ${r.data.settled_by || ""}`,
              "",
              "Balance settled in full. Thank you for your service.",
              "— Nisha Transport Corporation",
            ].join("\n");
            const digits = String(c.owner_mobile || c.vendor_mobile || c.driver_phone || "").replace(/\D/g, "");
            const target = digits ? (digits.length === 10 ? "91" + digits : digits) : "";
            window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
          };
          toast.success(`Challan ${String(c.challan_no || "").replace(/^VHC[-\s]?/i, "")} marked Settled`, {
            description: "Voucher opened for print — send the settlement details to the vehicle owner's WhatsApp.",
            duration: 12000,
            action: { label: "WhatsApp Owner", onClick: settleWa },
          });
          printVehiclePayment(c, {
            type: "Vehicle Balance Payment — Settlement",
            date: String(r.data.settled_at || "").slice(0, 10),
            amount: amt,
            mode: "Cash",
            by: r.data.settled_by || "",
            remarks: "Balance settled in full on settlement day",
          });
          settleWa();
        } else {
          toast.success("Challan re-opened");
        }
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  const [consignments, setConsignments] = useState([]);
  const [printTarget, setPrintTarget] = useState(null);
  const [blankRows, setBlankRows] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [docsSummary, setDocsSummary] = useState({});
  const [masterVehs, setMasterVehs] = useState([]);
  useEffect(() => {
    api.get("/vehicle-master").then((r) => setMasterVehs(r.data || [])).catch(() => {});
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [hireBudget, setHireBudget] = useState({ limit: 0, branches: {} });
  useEffect(() => {
    api.get("/settings/hire-budget").then((r) => setHireBudget({ limit: Number(r.data?.limit) || 0, branches: r.data?.branches || {}, carry_forward: !!r.data?.carry_forward })).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/vehicle-docs-summary").then((r) => setDocsSummary(r.data || {})).catch(() => {});
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [vaultTarget, setVaultTarget] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [podScores, setPodScores] = useState({});
  useEffect(() => {
    api.get("/hire-challans").then((r) => {
      const m = {};
      (r.data || []).forEach((c) => {
        const k = String(c.vendor_broker_name || c.owner_name || "").trim().toLowerCase();
        if (!k) return;
        m[k] = m[k] || { back: 0, out: 0, sumDays: 0 };
        const ld = String(c.loading_date || c.date || "").slice(0, 10);
        if (c.pod_ack_received) {
          m[k].back++;
          if (c.pod_ack_date && /^\d{4}-\d{2}-\d{2}$/.test(ld)) m[k].sumDays += Math.max(Math.floor((new Date(c.pod_ack_date) - new Date(ld)) / 86400000), 0);
        } else {
          m[k].out++;
        }
      });
      const out = {};
      Object.entries(m).forEach(([k, v]) => { out[k] = { ...v, avg: v.back ? Math.round((v.sumDays / v.back) * 10) / 10 : null }; });
      setPodScores(out);
    }).catch(() => {});
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [registerOpen, setRegisterOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const marketId = searchParams.get("from_market");
  const [marketPrefill, setMarketPrefill] = useState(null);
  const [contacts, setContacts] = useState({});
  const [staffNames, setStaffNames] = useState([]);
  useEffect(() => {
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/shipments").then((r) => setConsignments(r.data)).catch(() => {});
    api.get("/branch-contacts").then((r) => setContacts(r.data || {})).catch(() => {});
    api.get("/staff-master").then((r) => setStaffNames([...new Set((r.data || []).filter((s) => (s.status || "Active") !== "Inactive").map((s) => s.name).filter(Boolean))].sort())).catch(() => {});
    api.get("/vehicles").then((r) => setFleetVehicles(r.data || [])).catch(() => {});
    api.get("/market-vehicles").then((r) => setMarketAll(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!marketId) return;
    api.get("/market-vehicles").then((r) => {
      const m = (r.data || []).find((x) => x.id === marketId);
      if (!m) return;
      setMarketPrefill({
        market_request_id: m.id,
        vendor_broker_name: m.vendor_name || "",
        engaged_office: m.branch || "Chennai",
        branch: m.branch || "Chennai",
        origin: m.from_place || "",
        destination: m.destination || "",
        vehicle_type: m.vehicle_type || "",
        vehicle_no: m.vehicle_no || "",
        weight_kgs: m.weight || "",
        hire_amount: Number(m.vehicle_hire) || 0,
        booking_date: m.date || today(),
        remarks: `Market vehicle request — Customer: ${m.customer_name || ""}${m.freight ? `, Freight ₹${m.freight}` : ""}`,
      });
    }).catch(() => {});
  }, [marketId]);
  const challanWa = (r) => {
    const no = String(r.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—";
    const msg = [
      "*NTC — Vehicle Hire Challan*",
      `Challan No.: ${no}`,
      `Loading Date: ${fmtDate(r.loading_date || r.date)}`,
      `Vehicle No.: ${r.vehicle_no || "—"}`,
      `From: ${r.origin || "—"} → To: ${r.destination || "—"}`,
      "",
      `Vehicle Hire: ${inr(r.hire_amount)}`,
      ...(Number(r.unloading_charge) > 0 ? [`Unloading Charge: ${inr(r.unloading_charge)}`] : []),
      ...(Number(r.challan_charge) > 0 ? [`Challan Charge: ${inr(r.challan_charge)}`] : []),
      ...(Number(r.extra_weight_charge) > 0 ? [`Extra Weight Charge: ${inr(r.extra_weight_charge)}`] : []),
      ...(Number(r.extra_delivery_charge) > 0 ? [`Extra Delivery Charge: ${inr(r.extra_delivery_charge)}`] : []),
      `Advance Amount: ${inr(r.advance_paid)}`,
      ...(Number(r.deduction_amt) > 0 ? [`Deduction: ${inr(r.deduction_amt)}`] : []),
      ...(Number(r.less_tds) > 0 ? [`Less TDS: ${inr(r.less_tds)}`] : []),
      `Balance Amount: ${inr(r.balance)}`,
      "",
      "— Nisha Transport Corporation",
    ].join("\n");
    const ph = String(r.owner_mobile || r.vendor_mobile || r.driver_phone || "").replace(/\D/g, "");
    const num = ph ? `91${ph.slice(-10)}` : "";
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const vCode = (r) =>
    r.vendor_code ||
    vendors.find((v) => v.id === r.vendor_id)?.vendor_code ||
    vendors.find((v) => (v.name || "").trim().toLowerCase() === (r.vendor_broker_name || "").trim().toLowerCase())?.vendor_code ||
    "";
  const vendorOptions = vendors.map((v) => ({
    value: v.id,
    label: v.name + (v.pan ? ` · ${v.pan}` : ""),
    patch: { vendor_id: v.id, vendor_broker_name: v.name, vendor_code: v.vendor_code || "", vendor_pan: v.pan || "", vendor_gstin: v.gstin || "", vendor_tan: v.tan || "", vendor_mobile: v.phone || "", driver_phone: v.phone || "" },
  }));
  const [driverMs, setDriverMs] = useState([]);
  useEffect(() => {
    api.get("/driver-masters").then((r) => setDriverMs(r.data || [])).catch(() => {});
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const driverOptions = driverMs.map((d) => ({
    value: d.id,
    label: `${d.driver_name}${d.mobile ? ` · ${d.mobile}` : ""}${d.vehicle_no ? ` (${d.vehicle_no})` : ""}`,
    patch: { driver_name: d.driver_name || "", driver_phone: d.mobile || "", dl_no: d.license_no || "", dl_validity: d.dl_expiry || "" },
  }));
  const consignmentOptions = consignments.map((c) => {
    const lr = c.lr_no || c.consignment_no || "";
    return {
      value: c.id,
      label: `${lr}${c.origin || c.destination ? ` · ${c.origin || "?"}→${c.destination || "?"}` : ""}`,
      patch: {
        consignment_id: c.id,
        lr_no: lr,
        lr_date: c.lr_date || c.dispatch_date || "",
        cn_no: c.consignment_no || lr,
        consignment_no: c.consignment_no || "",
        origin: c.origin || "",
        destination: c.destination || "",
        no_of_packages: c.packages ? String(c.packages) : "",
        contents: c.goods || "",
        booking_date: c.dispatch_date || c.lr_date || "",
      },
    };
  });
  return (
    <>
    <CopyChoiceDialog
      target={printTarget}
      onClose={() => setPrintTarget(null)}
      onPrinted={(t) => {
        if (t?.blank) return;
        toast.info(`Challan ${String(t.challan_no || "").replace(/^VHC[-\s]?/i, "")} sent to print`, {
          description: `Send the challan summary to ${t.vendor_broker_name || t.owner_name || "the vehicle owner"}'s WhatsApp with one tap.`,
          duration: 12000,
          action: { label: "WhatsApp Owner", onClick: () => challanWa(t) },
        });
      }}
    />
    <BlankChallanDialog rows={blankRows} onClose={() => setBlankRows(null)} onPrint={(list) => setPrintTarget(list)} />
    <ChallanPaymentsDialog target={payTarget} onClose={() => setPayTarget(null)} onChanged={() => setReloadKey((k) => k + 1)} isAdmin={isAdmin} />
    <VehiclePaymentsRegisterDialog open={registerOpen} onClose={() => setRegisterOpen(false)} />
    {chargesRep ? <ExtraChargesReportDialog rows={chargesRep} onClose={() => setChargesRep(null)} /> : null}
    {chargeApprovals ? <ChargeApprovalsDialog rows={chargeApprovals.rows} focusId={chargeApprovals.focusId} onClose={() => setChargeApprovals(null)} onDecided={() => { chargeApprovals.load?.(); setChargeApprovals(null); }} /> : null}
    <PodPendingDialog open={podPendOpen} onClose={() => setPodPendOpen(false)} onAttach={() => setReloadKey((k) => k + 1)} />
    <EntityManager
      key={reloadKey}
      title="Vehicle Hire Challan"
      subtitle="Issue a hire challan when you engage a market/hired vehicle. Balance = hire − advance."
      endpoint="hire-challans"
      entityName="Challan"
      formExtraAfter="extra_charge_reason"
      formExtra={(form, setForm) => (
        <div className="-mt-1 flex flex-wrap items-center gap-1.5" data-testid="reason-quick-picks">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Quick:</span>
          {reasonPicks.map((t) => (
            <button
              key={t}
              type="button"
              className="rounded-full border border-border bg-slate-50 px-2.5 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-900 hover:text-white"
              onClick={() => setForm((f) => {
                const cur = String(f.extra_charge_reason || "").trim();
                if (cur.toLowerCase().includes(t.toLowerCase())) return f;
                return { ...f, extra_charge_reason: cur ? `${cur}, ${t}` : t };
              })}
              data-testid={`reason-pick-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {t}
            </button>
          ))}
        </div>
      )}
      confirmSave={(form, allRows, editing) => {
        const exNum = (v) => Number(v) || 0;
        const exTotal = exNum(form.unloading_charge) + exNum(form.challan_charge) + exNum(form.extra_weight_charge) + exNum(form.extra_delivery_charge);
        if (exTotal > 0 && !String(form.extra_charge_reason || "").trim()) {
          const reason = window.prompt(
            `Extra charges of ₹${exTotal.toLocaleString("en-IN")} entered (Unloading / Challan / Extra Weight / Extra Delivery).\n\nPlease note WHY the extra charge was added — this prints on the challan:`
          );
          if (reason === null) return false;
          if (!String(reason).trim()) {
            window.alert("A reason is required when extra charges are entered — the challan was not saved.");
            return false;
          }
          form.extra_charge_reason = String(reason).trim();
        }
        const chBranch = String(form.branch || form.office || form.engaged_office || "").trim();
        const effChLimit = Number(chargeBranchLims?.[chBranch]) || chargeLimit || 0;
        if (exTotal > 0 && effChLimit > 0 && exTotal > effChLimit) {
          if (!isAdmin) {
            const okQ = window.confirm(
              `⛔ EXTRA CHARGES NEED ADMIN APPROVAL\n\nThis challan's extra charges of ₹${exTotal.toLocaleString("en-IN")} cross the ₹${effChLimit.toLocaleString("en-IN")} approval limit${Number(chargeBranchLims?.[chBranch]) ? ` for ${chBranch}` : ""}.\n\nSubmit it to the admin's approval queue and save? (Cancel = don't save)`
            );
            if (!okQ) return false;
            if (form.charge_approval_status === "Rejected") form.charge_resubmit_count = (Number(form.charge_resubmit_count) || 0) + 1;
            form.charge_approval_status = "Pending";
            form.charge_reject_note = "";
            form.charge_submitted_at = new Date().toISOString();
            setTimeout(() => toast.info("Challan saved — extra charges sent to the admin's Charge Approvals queue"), 400);
            const nudge = window.confirm("WhatsApp the admin now so they can approve the extra charges quickly?");
            if (nudge) {
              const msg = [
                "*CHARGE APPROVAL NEEDED* ⚠",
                `Challan: ${String(form.challan_no || "").replace(/^VHC[-\s]?/i, "") || "(new)"}`,
                `Vehicle: ${form.vehicle_no || "—"}`,
                `Branch: ${chBranch || "—"}`,
                `Extra charges: ₹${exTotal.toLocaleString("en-IN")} (limit ₹${effChLimit.toLocaleString("en-IN")})`,
                form.extra_charge_reason ? `Reason: ${form.extra_charge_reason}` : "",
                "",
                "Please open Vehicle Hire Challan → Charge Approvals to approve.",
              ].filter(Boolean).join("\n");
              setTimeout(() => window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank"), 150);
            }
          } else {
            const okCl = window.confirm(
              `⚠ EXTRA CHARGES OVER LIMIT\n\nThis challan's extra charges total ₹${exTotal.toLocaleString("en-IN")} — above the ₹${effChLimit.toLocaleString("en-IN")} approval limit${Number(chargeBranchLims?.[chBranch]) ? ` for ${chBranch}` : ""}.\n\nApprove as admin and save? (recorded in the Correction Log Book)`
            );
            if (!okCl) return false;
            form.charge_approval_status = "Approved";
            form.charge_approved_at = new Date().toISOString();
            form.charge_approved_by = user?.name || "Admin";
            api.post("/hire-challans/charge-override-log", { challan_no: form.challan_no || "", vehicle_no: form.vehicle_no || "", amount: exTotal, limit: effChLimit, reason: form.extra_charge_reason || "" }).catch(() => {});
          }
        } else if (form.charge_approval_status === "Pending" || form.charge_approval_status === "Rejected") {
          form.charge_approval_status = "";
          form.charge_reject_note = "";
        }
        const lrSetOf = (d) => {
          const s = new Set();
          const addv = (v) => {
            v = String(v || "").replace(/^CN[-\s]?/i, "").trim().toLowerCase();
            if (v) s.add(v);
          };
          (d.lr_items || []).forEach((x) => addv(x.lr_no));
          (d.lr_list || []).forEach((x) => addv(x.lr_no));
          String(d.lr_no || "").split(",").forEach(addv);
          return s;
        };
        const mine = lrSetOf(form);
        if (mine.size) {
          for (const ch of allRows || []) {
            if (editing && ch.id === editing.id) continue;
            const hit = [...lrSetOf(ch)].filter((v) => mine.has(v));
            if (hit.length) {
              window.alert(`⛔ DUPLICATE LR\n\nLR ${hit.join(", ").toUpperCase()} already has Vehicle Hire Challan ${String(ch.challan_no || "").replace(/^VHC[-\s]?/i, "")}.\n\nDuplicate Vehicle Hire Challan for the same LR is not allowed.`);
              return false;
            }
          }
        }
        const d = String(form.ins_valid_upto || "").slice(0, 10);
        if (d && /^\d{4}-\d{2}-\d{2}$/.test(d) && d < today()) {
          const ok = window.confirm(
            `⚠ INSURANCE EXPIRED\n\nVehicle ${form.vehicle_no || ""} insurance (${[form.insurance_co, form.policy_no].filter(Boolean).join(" ") || "policy"}) expired on ${d.split("-").reverse().join("-")}.\n\nEngaging an uninsured vehicle is risky. Save this challan anyway?`
          );
          if (!ok) return false;
        }
        const normBl = (s) => String(s || "").replace(/\s+/g, "").toUpperCase();
        const blVeh = masterVehs.find((x) => normBl(x.vehicle_no) === normBl(form.vehicle_no) && String(x.blacklisted || "").toLowerCase() === "yes");
        if (blVeh) {
          const okBl = window.confirm(
            `⛔ BLACKLISTED VEHICLE\n\n${form.vehicle_no || ""} is blacklisted in the Vehicle Master.\n${blVeh.blacklist_reason ? `Reason: ${blVeh.blacklist_reason}\n` : ""}\nBooking it again needs admin approval. Save this challan anyway?`
          );
          if (!okBl) return false;
        }
        const fcd = String(form.fc_valid_upto || "").slice(0, 10);
        if (fcd && /^\d{4}-\d{2}-\d{2}$/.test(fcd) && fcd < today()) {
          const fcDate = fcd.split("-").reverse().join("-");
          if (!isAdmin) {
            window.alert(
              `⛔ FC LAPSED — DESPATCH BLOCKED\n\nVehicle ${form.vehicle_no || ""} Fitness Certificate expired on ${fcDate}.\n\nA challan cannot be saved for a vehicle with lapsed FC. Ask the admin to override, or update the FC Valid Upto date after renewal.`
            );
            return false;
          }
          const okFc = window.confirm(
            `⚠ FC LAPSED — ADMIN OVERRIDE\n\nVehicle ${form.vehicle_no || ""} FC expired on ${fcDate}.\n\nStaff are blocked from saving this challan. Override as admin and save anyway?`
          );
          if (!okFc) return false;
          api.post("/hire-challans/fc-override-log", { vehicle_no: form.vehicle_no || "", fc_valid_upto: fcd, challan_no: form.challan_no || "" }).catch(() => {});
        }
        const dmRc = (driverMs || []).find((x) => normBl(x.vehicle_no) === normBl(form.vehicle_no) && x.rc_expiry);
        const rcd = String(dmRc?.rc_expiry || "").slice(0, 10);
        if (rcd && /^\d{4}-\d{2}-\d{2}$/.test(rcd) && rcd < today()) {
          const rcDate = rcd.split("-").reverse().join("-");
          if (!isAdmin) {
            window.alert(
              `⛔ RC EXPIRED — DESPATCH BLOCKED\n\nVehicle ${form.vehicle_no || ""} Registration Certificate expired on ${rcDate} (per Driver Master).\n\nA challan cannot be saved for a vehicle with an expired RC. Ask the admin to override, or update the RC Expiry Date after renewal.`
            );
            return false;
          }
          const okRc = window.confirm(
            `⚠ RC EXPIRED — ADMIN OVERRIDE\n\nVehicle ${form.vehicle_no || ""} RC expired on ${rcDate}.\n\nStaff are blocked from saving this challan. Override as admin and save anyway?`
          );
          if (!okRc) return false;
          api.post("/hire-challans/fc-override-log", { vehicle_no: form.vehicle_no || "", fc_valid_upto: rcd, challan_no: form.challan_no || "", doc_type: "RC" }).catch(() => {});
        }
        const dld = String(form.dl_validity || "").slice(0, 10);
        if (dld && /^\d{4}-\d{2}-\d{2}$/.test(dld) && dld < today()) {
          const okDl = window.confirm(
            `⚠ DRIVER LICENCE EXPIRED\n\nDriver ${form.driver_name || ""} licence (${form.dl_no || "DL"}) expired on ${dld.split("-").reverse().join("-")}.\n\nSending a driver with an expired licence is a violation. Save this challan anyway?`
          );
          if (!okDl) return false;
        }
        const makeYear = parseInt(String(form.make || "").match(/(19|20)\d{2}/)?.[0] || "", 10);
        if (makeYear) {
          const age = new Date().getFullYear() - makeYear;
          if (age > 15) {
            const ok = window.confirm(
              `⚠ OLD VEHICLE\n\nVehicle ${form.vehicle_no || ""} make year is ${makeYear} — ${age} years old.\n\nMany routes restrict vehicles older than 15 years. Confirm route permits before engaging.\n\nSave this challan anyway?`
            );
            if (!ok) return false;
          }
        }
        const num = (v) => parseFloat(String(v ?? "").replace(/[^0-9.]/g, "")) || 0;
        const brName = String(form.branch || form.office || "").trim();
        const effBudget = Number(hireBudget.branches?.[brName]) || Number(hireBudget.limit) || 0;
        if (effBudget > 0 && brName) {
          const curM = today().slice(0, 7);
          const chMonth = String(form.loading_date || form.date || today()).slice(0, 7);
          if (chMonth === curM) {
            const spendIn = (m) => (allRows || [])
              .filter((c) => String(c.branch || c.office || "").trim().toLowerCase() === brName.toLowerCase()
                && String(c.loading_date || c.date || "").slice(0, 7) === m
                && (!editing || c.id !== editing.id))
              .reduce((a, c) => a + (Number(c.hire_amount) || 0), 0);
            const spent = spendIn(curM);
            const [cy, cm] = curM.split("-").map(Number);
            const prevM = cm === 1 ? `${cy - 1}-12` : `${cy}-${String(cm - 1).padStart(2, "0")}`;
            const carry = hireBudget.carry_forward ? Math.max(0, effBudget - spendIn(prevM)) : 0;
            const cap = effBudget + carry;
            const newHire = num(form.hire_amount);
            if (spent + newHire > cap) {
              const okBg = window.confirm(
                `⚠ HIRE BUDGET EXCEEDED\n\n${brName} has already spent ₹${spent.toLocaleString("en-IN")} on vehicle hire this month.\nThis challan's ₹${newHire.toLocaleString("en-IN")} takes it to ₹${(spent + newHire).toLocaleString("en-IN")} — over the ₹${cap.toLocaleString("en-IN")} budget${carry > 0 ? ` (₹${effBudget.toLocaleString("en-IN")} + ₹${carry.toLocaleString("en-IN")} carried from last month)` : ""}.\n\nSave this challan anyway?`
              );
              if (!okBg) return false;
              api.post("/hire-challans/budget-override-log", { branch: brName, challan_no: form.challan_no || "", vehicle_no: form.vehicle_no || "", spent, hire_amount: newHire, limit: cap }).catch(() => {});
            }
          }
        }
        const byLr = {};
        consignments.forEach((c) => {
          const k = String(c.lr_no || c.consignment_no || "").replace(/^CN[-\s]?/i, "").trim().toLowerCase();
          if (k) byLr[k] = c;
        });
        const lrRows = Array.isArray(form.lr_items) && form.lr_items.length
          ? form.lr_items
          : form.lr_no && !String(form.lr_no).includes(",")
            ? [{ lr_no: form.lr_no, packages: form.no_of_packages }]
            : [];
        const mism = [];
        lrRows.forEach((x) => {
          const c = byLr[String(x.lr_no || "").replace(/^CN[-\s]?/i, "").trim().toLowerCase()];
          if (!c || !c.packages) return;
          const entered = num(x.packages);
          const actual = num(c.packages);
          if (entered && actual && entered !== actual) mism.push(`• LR ${x.lr_no}: challan shows ${entered} pkg, consignment booked ${actual} pkg`);
        });
        if (mism.length) {
          return window.confirm(`⚠ LOADING TALLY MISMATCH\n\n${mism.join("\n")}\n\nPackages on this challan don't match the booked consignments. Save anyway?`);
        }
        return true;
      }}
      pageSize={20}
      onSaved={(saved) => {
        (async () => {
          try {
            const vno = String(saved?.vehicle_no || "").trim();
            if (!vno) return;
            const normV = (s) => String(s || "").replace(/\s+/g, "").toUpperCase();
            const res = await api.get("/vehicle-master");
            if ((res.data || []).some((x) => normV(x.vehicle_no) === normV(vno))) return;
            toast.info(`${vno} is not in the Vehicle Master yet`, {
              description: "Save it once — its details will auto-fill on every future challan.",
              duration: 15000,
              action: {
                label: "Save to Master",
                onClick: async () => {
                  try {
                    const pick = (k) => saved[k] || "";
                    await api.post("/vehicle-master", {
                      vehicle_no: vno,
                      vehicle_type: pick("vehicle_type"), make: pick("make"), model: pick("model"), model_type: pick("model_type"),
                      chassis_no: pick("chassis_no"), engine_no: pick("engine_no"),
                      owner_name: pick("owner_name"), owner_mobile: pick("owner_mobile"), pan_no: pick("pan_no"),
                      insurance_co: pick("insurance_co"), policy_no: pick("policy_no"),
                      ins_valid_upto: pick("ins_valid_upto"), permit_valid_upto: pick("permit_valid_upto"), fc_valid_upto: pick("fc_valid_upto"),
                      driver_name: pick("driver_name"), driver_phone: pick("driver_phone"), dl_no: pick("dl_no"), dl_validity: pick("dl_validity"),
                    });
                    toast.success(`${vno} saved to the Vehicle Master`);
                  } catch (e2) {
                    toast.error(formatApiError(e2.response?.data?.detail));
                  }
                },
              },
            });
          } catch (e) { /* offer optional */ }
        })();
        const cn = Number(saved?.credit_note) || 0;
        if (cn <= 0) return;
        if (saved.credit_note_status === "Pending") {
          toast.warning(`Credit Note ${inr(cn)} sent for admin approval`, {
            description: "It won't hit the ledger until the admin approves it. Alert the admin now?",
            duration: 15000,
            action: {
              label: "WhatsApp Admin",
              onClick: async () => {
                let phone = "";
                try { phone = (await api.get("/notes/admin-alert-contact")).data.phone || ""; } catch {}
                const d = String(phone).replace(/\D/g, "");
                const amsg = [
                  "*NTC — Note Approval Needed*",
                  "",
                  `Credit Note ${inr(cn)} submitted on Challan ${saved.challan_no || "—"}`,
                  `Vendor/Broker: ${saved.vendor_broker_name || saved.owner_name || "—"}`,
                  ...(saved.credit_note_reason ? [`Reason: ${saved.credit_note_reason}`] : []),
                  "",
                  "Please Approve/Reject in Outstanding → Approve Notes.",
                ].join("\n");
                window.open(d ? `https://wa.me/91${d.slice(-10)}?text=${encodeURIComponent(amsg)}` : `https://wa.me/?text=${encodeURIComponent(amsg)}`, "_blank");
              },
            },
          });
          return;
        }
        const ph = String(saved.vendor_mobile || saved.owner_mobile || saved.driver_phone || "").replace(/\D/g, "");
        const msg = [
          "*NISHA TRANSPORT CORPORATION*",
          "*Credit Note Issued*",
          "",
          `Challan No: ${saved.challan_no || "—"}`,
          `Vendor/Broker: ${saved.vendor_broker_name || saved.owner_name || "—"}`,
          `Vehicle: ${saved.vehicle_no || "—"}`,
          `Credit Note Amount: ${inr(cn)}`,
          ...(saved.credit_note_reason ? [`Reason: ${saved.credit_note_reason}`] : []),
          "",
          "This amount has been deducted from your hire payable.",
          "— Nisha Transport Corporation",
        ].join("\n");
        toast.info(`Credit Note ${inr(cn)} recorded on ${saved.challan_no || "challan"}`, {
          description: "Send the vendor/broker a WhatsApp confirmation?",
          duration: 12000,
          action: {
            label: "WhatsApp",
            onClick: () =>
              window.open(ph ? `https://wa.me/91${ph.slice(-10)}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank"),
          },
        });
      }}
      jumpField={{ label: "Jump to Challan No.", key: "challan_no" }}
      windowControls
      initialCreate={marketPrefill}
      initialEditId={searchParams.get("open") || ""}
      initialFilterVals={searchParams.get("branch") ? { branch: searchParams.get("branch") } : undefined}
      defaults={{
        loading_date: today(),
        vehicle_no: "",
        chassis_no: "",
        model: "",
        model_type: "",
        make: "",
        engine_no: "",
        insurance_co: "",
        policy_no: "",
        permit_valid_upto: "",
        ins_valid_upto: "",
        owner_name: "",
        owner_mobile: "",
        vendor_broker_name: "",
        vendor_code: "",
        vendor_mobile: "",
        vendor_pan: "",
        vendor_gstin: "",
        vendor_tan: "",
        vendor_id: "",
        pan_no: "",
        driver_name: "",
        driver_phone: "",
        dl_no: "",
        dl_validity: "",
        engaged_office: OFFICES.includes(user?.office) ? user.office : "Chennai",
        origin: "",
        from_address: "",
        destination: "",
        to_address: "",
        branch: OFFICES.includes(user?.office) ? user.office : "Chennai",
        cn_no: "",
        lr_no: "",
        lr_date: "",
        lr_list: [],
        lr_items: [],
        consignment_id: "",
        booking_date: today(),
        no_of_packages: "",
        contents: "",
        weight_kgs: "",
        vehicle_type: "",
        consignment_no: "",
        hire_amount: 0,
        hamali_charge: 0,
        unloading_charge: 0,
        challan_charge: 0,
        extra_weight_charge: 0,
        extra_delivery_charge: 0,
        extra_charge_reason: "",
        advance_paid: 0,
        deduction_amt: 0,
        credit_note: 0,
        credit_note_reason: "",
        less_tds: 0,
        less_odc: 0,
        payment_mode: "Cash",
        bank_name: "",
        remarks: "",
        loading_staff_name: "",
        branch_incharge_name: "",
        traffic_incharge_name: "",
        cashier_name: "",
        passed_by_name: "",
        date: today(),
      }}
      fields={[
        { key: "vehicle_no", label: "Vehicle No.", type: "text", placeholder: "RJ14 GA 4321",
          autoFill: async (v, form) => {
            const norm = (s) => String(s || "").replace(/\s+/g, "").toUpperCase();
            const key = norm(v);
            if (!key) return null;
            const patch = {};
            try {
              const vmRes = await api.get("/vehicle-master");
              const vm = (vmRes.data || []).find((x) => norm(x.vehicle_no) === key);
              if (vm) {
                if (String(vm.blacklisted || "").toLowerCase() === "yes") {
                  toast.error(`⛔ BLACKLISTED VEHICLE — ${v.trim()}`, {
                    description: vm.blacklist_reason ? `Reason: ${vm.blacklist_reason}. Do not book this vehicle without admin approval.` : "This vehicle is marked as a problem vehicle in the Vehicle Master. Do not book it without admin approval.",
                    duration: 15000,
                  });
                }
                ["vehicle_type", "make", "model", "model_type", "chassis_no", "engine_no", "owner_name", "owner_mobile", "pan_no", "insurance_co", "policy_no", "ins_valid_upto", "permit_valid_upto", "fc_valid_upto", "driver_name", "driver_phone", "dl_no", "dl_validity"].forEach((k2) => {
                  if (vm[k2]) patch[k2] = vm[k2];
                });
                toast.success(`Vehicle details filled from Vehicle Master for ${v.trim()}`);
              }
            } catch (e) { /* master optional */ }
            try {
              const dmRes = await api.get("/driver-masters");
              const dm = (dmRes.data || [])
                .filter((x) => norm(x.vehicle_no) === key)
                .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))[0];
              if (dm) {
                const dmap = [["driver_name", dm.driver_name], ["driver_phone", dm.mobile], ["dl_no", dm.license_no], ["dl_validity", dm.dl_expiry]];
                const filledDm = [];
                dmap.forEach(([k2, val]) => {
                  if (val && !patch[k2] && !form[k2]) {
                    patch[k2] = val;
                    filledDm.push(k2);
                  }
                });
                if (filledDm.length) toast.success(`Driver details filled from Driver Master (${dm.dm_no || dm.driver_name}) for ${v.trim()}`);
              }
            } catch (e) { /* driver master optional */ }
            try {
              const vd = await api.get(`/vehicle-docs/${encodeURIComponent(v.trim())}`);
              const map = { Insurance: "ins_valid_upto", Permit: "permit_valid_upto", FC: "fc_valid_upto", DL: "dl_validity" };
              const filled = [];
              (vd.data || []).forEach((d) => {
                const fk = map[d.doc_type];
                if (fk && d.valid_upto) {
                  patch[fk] = d.valid_upto;
                  filled.push(d.doc_type);
                }
              });
              if (filled.length) toast.success(`${filled.join(", ")} valid-upto filled from the Document Vault of ${v.trim()}`);
            } catch (e) { /* vault optional */ }
            if (!form.vehicle_type) {
              const cv = fleetVehicles.find((x) => norm(x.registration_no) === key);
              const mv = marketAll.filter((x) => norm(x.vehicle_no) === key && x.vehicle_type).sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")))[0];
              const t = cv?.model_type || cv?.type || mv?.vehicle_type || "";
              if (t) patch.vehicle_type = t;
            }
            try {
              const res = await api.get("/hire-challans");
              const prev = (res.data || [])
                .filter((c) => norm(c.vehicle_no) === key && c.id !== form.id)
                .sort((a, b) => String(b.loading_date || b.date || b.created_at || "").localeCompare(String(a.loading_date || a.date || a.created_at || "")))[0];
              if (prev) {
                [["owner_name", prev.owner_name], ["owner_mobile", prev.owner_mobile], ["pan_no", prev.pan_no], ["insurance_co", prev.insurance_co], ["policy_no", prev.policy_no], ["ins_valid_upto", prev.ins_valid_upto], ["permit_valid_upto", prev.permit_valid_upto], ["fc_valid_upto", prev.fc_valid_upto], ["vehicle_type", prev.vehicle_type]].forEach(([k, val]) => {
                  if (val && !form[k] && !patch[k]) patch[k] = val;
                });
              }
              const ownerFilled = prev && (patch.owner_name || patch.owner_mobile || patch.insurance_co || patch.policy_no);
              if (ownerFilled) toast.success(`Owner & insurance details filled from last challan ${String(prev.challan_no || "").replace(/^VHC[-\s]?/i, "") || ""} of ${v.trim()}`);
              else if (patch.vehicle_type) toast.success(`Vehicle Type auto-filled: ${patch.vehicle_type}`);
            } catch (e) {
              if (patch.vehicle_type) toast.success(`Vehicle Type auto-filled: ${patch.vehicle_type}`);
            }
            return Object.keys(patch).length ? patch : null;
          } },
        { key: "chassis_no", label: "Chassis No.", type: "text" },
        { key: "engine_no", label: "Engine No.", type: "text" },
        { key: "model", label: "Vehicle Model", type: "text" },
        { key: "model_type", label: "Vehicle Model Type", type: "text" },
        { key: "make", label: "Vehicle Make Year", type: "text",
          autoFill: async (v) => {
            const y = parseInt(String(v || "").match(/(19|20)\d{2}/)?.[0] || "", 10);
            if (y) {
              const age = new Date().getFullYear() - y;
              if (age > 15) {
                toast.warning(`⚠ OLD VEHICLE — make year ${y} is ${age} years old`, {
                  description: "Many routes restrict vehicles older than 15 years. Confirm route permits before engaging.",
                  duration: 12000,
                });
              }
            }
            return null;
          } },
        { key: "insurance_co", label: "Insurance Company Name", type: "text" },
        { key: "policy_no", label: "Insurance Policy No.", type: "text" },
        { key: "ins_valid_upto", label: "Insurance Valid Upto", type: "date" },
        { key: "permit_valid_upto", label: "Permit Valid upto", type: "date" },
        { key: "fc_valid_upto", label: "FC (Fitness Certificate) Valid Upto", type: "date" },
        { key: "owner_name", label: "Vehicle Owner Name", type: "text" },
        { key: "owner_mobile", label: "Vehicle Owner Mobile No.", type: "text" },
        { key: "_vendor_pick", label: "Pick from Vendor Master (auto-fills code, name, PAN, GSTIN, TAN, phone)", type: "lookup", options: vendorOptions, placeholder: vendorOptions.length ? "Select a vendor/broker" : "No vendors in master yet",
          applyPatch: (opt) => {
            if (!opt) return {};
            const name = opt.patch?.vendor_broker_name || "";
            const s = podScores[String(name).trim().toLowerCase()];
            if (s && s.avg != null && s.avg > 15) {
              toast.warning(`⚠ ${name} is a SLOW POD returner — avg ${s.avg} days to return signed PODs${s.out ? ` · ${s.out} still out` : ""}`, {
                description: "Get a POD return commitment before engaging this vendor.",
                duration: 12000,
              });
            } else if (s && s.out >= 2 && s.avg == null) {
              toast.warning(`⚠ ${name} has ${s.out} signed POD(s) still not returned`, { duration: 10000 });
            }
            return opt.patch || {};
          } },
        { key: "vendor_broker_name", label: "Vendor/Broker Name", type: "text" },
        { key: "vendor_code", label: "Vendor/Broker Code", type: "text", placeholder: "Auto from Vendor Master" },
        { key: "vendor_mobile", label: "Vendor/Broker Mobile No.", type: "text" },
        { key: "vendor_pan", label: "Vendor/Broker PAN No.", type: "text" },
        { key: "vendor_gstin", label: "Vendor/Broker GSTIN No.", type: "text" },
        { key: "vendor_tan", label: "Vendor/Broker TAN No.", type: "text" },
        { key: "pan_no", label: "Vehicle Owner PAN No.", type: "text" },
        { key: "_driver_pick", label: "Pick from Driver Master (auto-fills name, mobile & DL — for vehicles with multiple drivers)", type: "lookup", options: driverOptions, placeholder: driverOptions.length ? "Select a driver" : "No drivers in Driver Master yet",
          applyPatch: (opt) => (opt?.patch ? { ...opt.patch } : {}) },
        { key: "driver_name", label: "Driver Name", type: "text" },
        { key: "driver_phone", label: "Driver Mobile No.", type: "text" },
        { key: "dl_no", label: "Driving License No.", type: "text" },
        { key: "dl_validity", label: "Driving License Validity", type: "date" },
        { key: "engaged_office", label: "Engaged Office Name", type: "select", options: OFFICES },
        { key: "loading_date", label: "Loading Date", type: "date" },
        { key: "origin", label: "From", type: "text", placeholder: "Delhi" },
        { key: "destination", label: "To", type: "text", placeholder: "Mumbai" },
        { key: "branch", label: "Branch", type: "select", options: OFFICES },
        {
          key: "_consignment_pick",
          label: "Link Consignment — pick again to add more LRs to this vehicle",
          type: "lookup",
          options: consignmentOptions,
          placeholder: consignmentOptions.length ? "Select a consignment / LR" : "No consignments yet",
          applyPatch: (opt, prev) => {
            if (!opt) return {};
            const p = opt.patch;
            const list = Array.isArray(prev.lr_list) ? prev.lr_list : [];
            if (list.some((x) => x.consignment_id === p.consignment_id)) return {};
            const entry = { consignment_id: p.consignment_id, lr_no: p.lr_no, lr_date: p.lr_date, origin: p.origin, destination: p.destination };
            const newList = [...list, entry];
            const joined = newList.map((x) => x.lr_no).join(", ");
            const items = Array.isArray(prev.lr_items) ? prev.lr_items : [];
            const item = { branch: prev.branch || prev.engaged_office || "Chennai", lr_no: p.lr_no, lr_date: p.lr_date, packages: p.no_of_packages || "", contents: p.contents || "", weight: "" };
            const newItems = items.some((x) => x.lr_no === p.lr_no) ? items : [...items, item];
            if (newList.length === 1) return { ...p, lr_list: newList, lr_items: newItems };
            return { lr_list: newList, lr_items: newItems, lr_no: joined, cn_no: joined, consignment_no: joined };
          },
        },
        {
          key: "lr_items",
          label: "LR Entries — multiple LRs in this challan (auto-added from picker, or add rows manually)",
          type: "items",
          addLabel: "Add LR Row",
          columns: [
            { key: "branch", label: "Branch", placeholder: "Chennai", default: (f) => f.branch || f.engaged_office || "Chennai" },
            { key: "lr_no", label: "LR No." },
            { key: "lr_date", label: "LR Date", type: "date" },
            { key: "packages", label: "Packages" },
            { key: "contents", label: "Contents (Material)" },
            { key: "weight", label: "Weight" },
          ],
        },
        { key: "lr_no", label: "LR No. (all linked LRs)", type: "text", placeholder: "Auto from consignment" },
        { key: "lr_date", label: "LR Date", type: "date" },
        { key: "booking_date", label: "Booking Date", type: "date" },
        { key: "no_of_packages", label: "No. of Packages", type: "text" },
        { key: "contents", label: "Contents (Material)", type: "text", placeholder: "General cargo" },
        { key: "weight_kgs", label: "Weight in Kgs", type: "text" },
        { key: "vehicle_type", label: "Type of Vehicle", type: "text", placeholder: "6-Wheeler" },
        { key: "hire_amount", label: "Vehicle Hire (₹)", type: "number", placeholder: "45000",
          autoFill: async (v, form) => {
            try {
              const nm = (s) => String(s || "").trim().toLowerCase();
              if (!nm(form.origin) || !nm(form.destination)) return null;
              const [res, chRes] = await Promise.all([
                api.get("/freight-master").catch(() => ({ data: [] })),
                api.get("/hire-challans").catch(() => ({ data: [] })),
              ]);
              const hits = (res.data || []).filter((r) => nm(r.origin) === nm(form.origin) && nm(r.destination) === nm(form.destination) && Number(r.freight_amount) > 0);
              if (hits.length) {
                const best = hits.find((r) => r.unit && nm(r.unit) === nm(form.vehicle_type)) || hits[0];
                const rate = Number(best.freight_amount) || 0;
                const entered = parseFloat(String(v ?? "").replace(/[^0-9.]/g, "")) || 0;
                const diff = entered - rate;
                toast.info(`Freight Master rate ${form.origin} → ${form.destination}: ₹${rate.toLocaleString("en-IN")}${best.unit ? ` (${best.unit})` : ""}`, {
                  description: entered
                    ? diff === 0
                      ? "Entered hire matches the saved rate."
                      : `Entered hire is ₹${Math.abs(diff).toLocaleString("en-IN")} ${diff > 0 ? "ABOVE" : "below"} the saved rate${best.customer_name ? ` (${best.customer_name})` : ""}.`
                    : `Saved rate${best.customer_name ? ` for ${best.customer_name}` : ""} — use as a hire benchmark.`,
                  duration: 12000,
                });
              }
              const past = (chRes.data || [])
                .filter((c) => nm(c.origin) === nm(form.origin) && nm(c.destination) === nm(form.destination) && Number(c.hire_amount) > 0 && c.id !== form.id)
                .sort((a, b) => String(b.loading_date || b.date || "").localeCompare(String(a.loading_date || a.date || "")))
                .slice(0, 3);
              if (past.length) {
                toast.info(`Route hire trend — last ${past.length} hire(s) on ${form.origin} → ${form.destination}`, {
                  description: past
                    .map((c) => `₹${Number(c.hire_amount).toLocaleString("en-IN")} · ${c.vehicle_no || "—"} · ${fmtDate(String(c.loading_date || c.date || "").slice(0, 10))}`)
                    .join("   |   "),
                  duration: 12000,
                });
              }
            } catch (e) { /* hint optional */ }
            return null;
          } },
        { key: "hamali_charge", label: "Loading Charge (₹)", type: "number" },
        { key: "unloading_charge", label: "Unloading Charge (₹) — added to Balance", type: "number" },
        { key: "challan_charge", label: "Challan Charge (₹) — added to Balance", type: "number" },
        { key: "extra_weight_charge", label: "Extra Weight Charge (₹) — added to Balance", type: "number" },
        { key: "extra_delivery_charge", label: "Extra Delivery Charge (₹) — added to Balance", type: "number" },
        { key: "extra_charge_reason", label: "Extra Charges Reason / Note (prints on challan)", type: "text" },
        { key: "advance_paid", label: "Advance Amount (₹)", type: "number" },
        { key: "deduction_amt", label: "Deduction Amount (₹)", type: "number" },
        { key: "credit_note", label: "Credit Note — Vendor/Broker Deduction (₹)", type: "number" },
        { key: "credit_note_reason", label: "Credit Note Reason", type: "text", placeholder: "e.g. Shortage / Damage / Rate difference" },
        { key: "less_tds", label: "Less TDS (₹)", type: "number" },
        { key: "less_odc", label: "ODC Amount (₹) — added to Vehicle Hire", type: "number" },
        { key: "payment_mode", label: "Payment mode", type: "select", options: MODES },
        { key: "bank_name", label: "Bank Name", type: "text" },
        { key: "remarks", label: "Remarks", type: "textarea", placeholder: "Notes" },
        { key: "loading_staff_name", label: "Loading Staff Name", type: "text", placeholder: "For signature line", suggestions: staffNames },
        { key: "branch_incharge_name", label: "Branch Incharge Name", type: "text", placeholder: "For signature line", suggestions: staffNames },
        { key: "traffic_incharge_name", label: "Traffic In Charge Name", type: "text", placeholder: "For signature line", suggestions: staffNames },
        { key: "cashier_name", label: "Cashier Name", type: "text", placeholder: "For signature line", suggestions: staffNames },
        { key: "passed_by_name", label: "Passed By Name", type: "text", placeholder: "For signature line", suggestions: staffNames },
        { key: "date", label: "Challan Date", type: "date" },
      ]}
      columns={[
        { key: "_sl", label: "SL No.", render: (r, idx) => (typeof idx === "number" ? idx + 1 : "—") },
        { key: "engaged_office", label: "Branch", render: (r) => r.engaged_office || r.office || "—" },
        { key: "challan_no", label: "Challan No.", render: (r) => (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            {String(r.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—"}
            {r.charge_approval_status === "Pending" && (
              <button
                type="button"
                className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700 hover:bg-amber-200"
                title={isAdmin ? "Tap to open this challan in the Charge Approvals queue" : "Extra charges over the branch limit — waiting in the admin's Charge Approvals queue"}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isAdmin) setChargeApprovals({ focusId: r.id, load: () => setReloadKey((k) => k + 1) });
                  else toast.info("Waiting for the admin to approve these extra charges from the Charge Approvals queue.");
                }}
                data-testid="charges-pending-badge"
              >
                <AlertTriangle className="h-2.5 w-2.5" /> charges pending
              </button>
            )}
            {r.charge_approval_status === "Rejected" && (
              <>
              <button
                type="button"
                className="inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-red-700 hover:bg-red-200"
                title={r.charge_reject_note ? `Admin's note: ${r.charge_reject_note}` : "Extra charges rejected by the admin"}
                onClick={(e) => {
                  e.stopPropagation();
                  window.alert(`⛔ EXTRA CHARGES REJECTED\n\nChallan ${String(r.challan_no || "").replace(/^VHC[-\s]?/i, "")}\n\nAdmin's note: ${r.charge_reject_note || "(no note)"}\n\nUse Fix & Resubmit to correct the charges.`);
                }}
                data-testid="charges-rejected-badge"
              >
                <AlertTriangle className="h-2.5 w-2.5" /> charges rejected
              </button>
              <button
                type="button"
                className="inline-flex items-center rounded-full border border-blue-300 bg-blue-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-blue-700 hover:bg-blue-100"
                title={`Open this challan to correct its charges and resubmit${r.charge_reject_note ? ` — admin's note: ${r.charge_reject_note}` : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchParams({ open: r.id });
                }}
                data-testid="fix-resubmit-btn"
              >
                Fix &amp; Resubmit
              </button>
              </>
            )}
          </span>
        ) },
        { key: "date", label: "Loading Date", render: (r) => fmtDate(r.loading_date || r.date) },
        { key: "vehicle_no", label: "Vehicle No." },
        { key: "vehicle_type", label: "Vehicle Type", render: (r) => r.vehicle_type || "—" },
        { key: "owner_name", label: "Vehicle Owner Name" },
        { key: "owner_mobile", label: "Vehicle Owner Mobile No.", render: (r) => {
          const ph = String(r.owner_mobile || "").replace(/\D/g, "");
          if (!ph) return "—";
          return (
            <span className="inline-flex items-center gap-1.5" data-testid="challan-owner-mobile">
              <a href={`tel:+91${ph.slice(-10)}`} className="font-medium text-primary underline underline-offset-2" onClick={(e) => e.stopPropagation()} data-testid="challan-owner-call-link">
                {r.owner_mobile}
              </a>
              <a
                href={`https://wa.me/91${ph.slice(-10)}`}
                target="_blank"
                rel="noreferrer"
                className="grid h-6 w-6 place-items-center rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                title="WhatsApp vehicle owner"
                onClick={(e) => e.stopPropagation()}
                data-testid="challan-owner-whatsapp-link"
              >
                <MessageCircle className="h-3.5 w-3.5" />
              </a>
            </span>
          );
        } },
        { key: "driver_phone", label: "Driver Phone", render: (r) => {
          const ph = String(r.driver_phone || "").replace(/\D/g, "");
          if (!ph) return "—";
          return (
            <span className="inline-flex items-center gap-1.5" data-testid="challan-driver-phone">
              <a href={`tel:+91${ph.slice(-10)}`} className="font-medium text-primary underline underline-offset-2" onClick={(e) => e.stopPropagation()} data-testid="challan-driver-call-link">
                {r.driver_phone}
              </a>
              <a
                href={`https://wa.me/91${ph.slice(-10)}`}
                target="_blank"
                rel="noreferrer"
                className="grid h-6 w-6 place-items-center rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                title="WhatsApp driver"
                onClick={(e) => e.stopPropagation()}
                data-testid="challan-driver-whatsapp-link"
              >
                <MessageCircle className="h-3.5 w-3.5" />
              </a>
            </span>
          );
        } },
        { key: "vendor_broker_name", label: "Vendor/Broker Name", render: (r) => r.vendor_broker_name || "—" },
        { key: "vendor_code", label: "Vendor/Broker Code", render: (r) => vCode(r) || "—" },
        { key: "origin", label: "From", render: (r) => r.origin || "—" },
        { key: "destination", label: "Destination", render: (r) => r.destination || "—" },
        { key: "hire_amount", label: "Vehicle Hire", align: "right", render: (r) => inr(r.hire_amount) },
        { key: "advance_paid", label: "Advance Amount", align: "right", render: (r) => inr(r.advance_paid) },
        { key: "balance", label: "Balance Amount", align: "right", render: (r) => inr(r.balance) },
        { key: "pod", label: "POD", render: (r) => <PodCell row={r} onChanged={() => setReloadKey((k) => k + 1)} /> },
        { key: "vehicle_docs", label: "Attached", render: (r) => {
          const k = String(r.vehicle_no || "").replace(/\s+/g, "").toUpperCase();
          const types = docsSummary[k] || [];
          const missing = ["RC", "FC"].filter((t) => !types.includes(t));
          return (
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <button
                type="button"
                className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold ${types.length ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                title={types.length ? `Attached: ${types.join(", ")} — tap to view the Vehicle Document Vault` : "No documents attached — tap to add RC, Insurance, Permit & FC copies"}
                onClick={(e) => { e.stopPropagation(); setVaultTarget(r); }}
                data-testid="challan-vault-btn"
              >
                <Paperclip className="h-3 w-3" /> {types.length ? `${types.length}/6` : "Add"}
              </button>
              {missing.length ? (
                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800" title={`${missing.join(" & ")} copy not in the vault — collect from the driver at loading`} data-testid="challan-docs-missing-chip">
                  No {missing.join("·")}
                </span>
              ) : null}
            </span>
          );
        } },
        { key: "pod_ack", label: "POD Ack", render: (r) => <PodAckCell row={r} onChanged={() => setReloadKey((k) => k + 1)} /> },
        {
          key: "payments",
          label: "Payments",
          render: (r) => {
            const types = new Set((r.payments || []).map((p) => p.type));
            const balDue = Number(r.balance) > 0 && !types.has("Vehicle Balance Payment") && !r.settled;
            const chip = (label, key) =>
              types.has(key) ? (
                <span key={key} className="mr-1 inline-flex rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700" data-testid="challan-payment-chip">
                  {label}
                </span>
              ) : null;
            if (!types.size && !balDue && !r.settled) return <span className="text-xs text-muted-foreground">—</span>;
            return (
              <span className="whitespace-nowrap">
                {chip("Hire Paid", "Vehicle Hire Payment")}
                {chip("Adv Paid", "Vehicle Advance Payment")}
                {chip("Bal Paid", "Vehicle Balance Payment")}
                {r.settled && (
                  <span className="mr-1 inline-flex rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700" title={`Settled on ${String(r.settled_at || "").slice(0, 10)} by ${r.settled_by || ""}`} data-testid="challan-settled-chip">
                    Settled ✓
                  </span>
                )}
                {balDue && (
                  <span className="inline-flex rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700" data-testid="challan-bal-due-chip">
                    Bal Due
                  </span>
                )}
                {(() => {
                  const s = podScores[String(r.vendor_broker_name || r.owner_name || "").trim().toLowerCase()];
                  const hold = balDue && s && s.avg != null && s.avg > 15 && s.out > 0 && !r.balance_release_approved;
                  if (hold) {
                    return (
                      <>
                        <span className="ml-1 inline-flex rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800" title={`Balance payment on hold — slow POD vendor (avg ${s.avg}d, ${s.out} POD(s) still out). Admin approval required.`} data-testid="challan-bal-hold-chip">
                          Bal Hold 🔒
                        </span>
                        {isAdmin && (
                          <button
                            type="button"
                            className="ml-1 rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-100"
                            title="Approve balance release for this challan"
                            data-testid="challan-approve-balance-btn"
                            onClick={async (e) => {
                              e.stopPropagation();
                              const note = window.prompt("Reason for releasing this balance (shown in the Correction Log Book):", "");
                              if (note === null) return;
                              try {
                                await api.post(`/hire-challans/${r.id}/approve-balance`, { reason: note });
                                toast.success(`Balance release approved for challan ${String(r.challan_no || "").replace(/^VHC[-\s]?/i, "")}`);
                                setReloadKey((k) => k + 1);
                              } catch (err) {
                                toast.error(formatApiError(err.response?.data?.detail));
                              }
                            }}
                          >
                            Approve
                          </button>
                        )}
                      </>
                    );
                  }
                  if (r.balance_release_approved && balDue) {
                    return (
                      <span className="ml-1 inline-flex rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-700" title={`Balance release approved by ${r.balance_release_by || "admin"}`} data-testid="challan-bal-approved-chip">
                        Bal Appr ✓
                      </span>
                    );
                  }
                  return null;
                })()}
              </span>
            );
          },
        },
        {
          key: "ins_valid_upto",
          label: "Insurance",
          render: (r) => {
            const d = r.ins_valid_upto;
            const soon = (dt) => dt && /^\d{4}-\d{2}-\d{2}$/.test(String(dt)) && !isExpired(dt) && (new Date(dt) - new Date(today())) / 86400000 <= 30;
            const insAlert = d && (isExpired(d) || soon(d));
            const permitAlert = r.permit_valid_upto && (isExpired(r.permit_valid_upto) || soon(r.permit_valid_upto));
            const ph = String(r.owner_mobile || r.vendor_mobile || r.driver_phone || "").replace(/\D/g, "");
            const remAt = reminded[r.id] || r.ins_reminder_at;
            const waBtn = (insAlert || permitAlert) && ph ? (
              <button
                type="button"
                className={`grid h-6 w-6 cursor-pointer place-items-center rounded-full ${remAt ? "bg-slate-200 text-slate-500 hover:bg-slate-300" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"}`}
                title={remAt ? `Already reminded on ${fmtDate(String(remAt).slice(0, 10))} — click to send again` : "WhatsApp renewal reminder to owner"}
                onClick={(e) => {
                  e.stopPropagation();
                  const line = (label, dt) => `${label}: ${isExpired(dt) ? "EXPIRED on" : "expires on"} ${fmtDate(dt)}`;
                  const msg = [
                    "⚠ *NTC — Renewal Reminder*",
                    `Vehicle No.: ${r.vehicle_no || "—"}`,
                    ...(insAlert ? [line(`Insurance${r.policy_no ? ` (Policy ${r.policy_no})` : ""}`, d)] : []),
                    ...(permitAlert ? [line("Permit", r.permit_valid_upto)] : []),
                    "",
                    "Please renew immediately and share the updated documents.",
                    "— Nisha Transport Corporation",
                  ].join("\n");
                  window.open(`https://wa.me/91${ph.slice(-10)}?text=${encodeURIComponent(msg)}`, "_blank");
                  api.put(`/hire-challans/${r.id}/reminder`).then((res) => {
                    setReminded((m) => ({ ...m, [r.id]: res.data?.ins_reminder_at || new Date().toISOString() }));
                  }).catch(() => {});
                }}
                data-testid="insurance-expiry-wa-btn"
              >
                <MessageCircle className="h-3.5 w-3.5" />
              </button>
            ) : null;
            const remChip = remAt ? (
              <span className="whitespace-nowrap rounded bg-slate-100 px-1 py-0.5 text-[10px] font-semibold text-slate-500" title="Renewal reminder already sent" data-testid="reminder-sent-chip">
                Reminded {fmtDate(String(remAt).slice(0, 10))}
              </span>
            ) : null;
            const badge = !d ? "—" : isExpired(d) ? (
              <span className="inline-flex items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-700" data-testid="insurance-expired-badge">
                <AlertTriangle className="h-3 w-3" /> Expired · {fmtDate(d)}
              </span>
            ) : soon(d) ? (
              <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700" data-testid="insurance-expiring-badge">
                <AlertTriangle className="h-3 w-3" /> {fmtDate(d)}
              </span>
            ) : (
              <span className="text-xs text-slate-600">{fmtDate(d)}</span>
            );
            return <span className="inline-flex items-center gap-1.5">{badge}{waBtn}{remChip}</span>;
          },
        },
      ]}
      filters={[
        {
          key: "q",
          label: "Search",
          type: "text",
          placeholder: "Vendor / code / vehicle / challan / 08-2026",
          match: (r, v) => {
            const q = String(v || "").trim().toLowerCase();
            if (!q) return true;
            const tDate = parseTermDate(q);
            const tMonth = !tDate ? parseTermMonth(q) : null;
            if (tDate) return String(r.loading_date || r.date || "").slice(0, 10) === tDate;
            if (tMonth) return String(r.loading_date || r.date || "").slice(0, 7) === tMonth;
            return [r.vendor_broker_name, vCode(r), r.vehicle_no, r.owner_name, String(r.challan_no || "").replace(/^VHC[-\s]?/i, ""), r.origin, r.destination]
              .some((x) => String(x || "").toLowerCase().includes(q));
          },
        },
        { key: "from_date", label: "From (Loading)", type: "date", match: (r, v) => !v || String(r.loading_date || r.date || "") >= v },
        { key: "to_date", label: "To (Loading)", type: "date", match: (r, v) => !v || String(r.loading_date || r.date || "") <= v },
        { key: "branch", label: "Branch", type: "select", options: ["All Branches", ...OFFICES], match: (r, v) => !v || v === "All Branches" || (r.engaged_office || r.office) === v },
        {
          key: "fy",
          label: "Financial Year",
          type: "select",
          options: FY_FILTER_OPTIONS,
          match: (r, v) => {
            if (!v || v === "All FY") return true;
            const y = Number(v.slice(3, 7));
            const d = String(r.loading_date || r.date || "");
            return d >= `${y}-04-01` && d <= `${y + 1}-03-31`;
          },
        },
      ]}
      exportCsv={exportChallansCsv}
      headerExtra={(rows, shown, fvals, load, setFvals) => {
        const q = String(fvals?.q || "").trim();
        const srchBanner = q ? (() => {
          const hire = (shown || []).reduce((s, r) => s + (Number(r.hire_amount) || 0), 0);
          const bal = (shown || []).reduce((s, r) => s + (Number(r.balance ?? (Number(r.hire_amount) || 0) - (Number(r.advance_paid) || 0)) || 0), 0);
          const first = (shown || [])[0];
          const vphone = String(
            first?.vendor_mobile ||
            vendors.find((v) => v.id === first?.vendor_id)?.phone ||
            vendors.find((v) => (v.name || "").trim().toLowerCase() === (first?.vendor_broker_name || "").trim().toLowerCase())?.phone ||
            ""
          ).replace(/\D/g, "");
          const sendVendorBalance = async () => {
            const rBal = (r) => Number(r.balance ?? (Number(r.hire_amount) || 0) - (Number(r.advance_paid) || 0)) || 0;
            const pend = (shown || []).filter((r) => rBal(r) > 0);
            const list = pend.length ? pend : shown || [];
            const podLinks = {};
            await Promise.all(
              list.filter((r) => r.pod_path).slice(0, 10).map(async (r) => {
                try {
                  const res = await api.post(`/hire-challans/${r.id}/pod-share`);
                  podLinks[r.id] = `${window.location.origin}/api/public/challan-pod/${res.data.id}`;
                } catch (e) { /* skip link on failure */ }
              })
            );
            const rExtras = (r) => (Number(r.unloading_charge) || 0) + (Number(r.challan_charge) || 0) + (Number(r.extra_weight_charge) || 0) + (Number(r.extra_delivery_charge) || 0);
            const totUnl = list.reduce((s, r) => s + (Number(r.unloading_charge) || 0), 0);
            const totChg = list.reduce((s, r) => s + (Number(r.challan_charge) || 0), 0);
            const totWt = list.reduce((s, r) => s + (Number(r.extra_weight_charge) || 0), 0);
            const totDly = list.reduce((s, r) => s + (Number(r.extra_delivery_charge) || 0), 0);
            const totEx = totUnl + totChg + totWt + totDly;
            const msg = [
              "*NTC — Vendor Balance Summary*",
              `Vendor: ${first?.vendor_broker_name || q}${vCode(first || {}) ? ` (${vCode(first || {})})` : ""}`,
              "",
              pend.length ? "Pending challans:" : "Challans:",
              ...list.map((r, i) => `${i + 1}. Challan ${String(r.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—"} — ${fmtDate(r.loading_date || r.date)} — ${r.vehicle_no || "—"} — ${r.origin || "?"}→${r.destination || "?"}: Hire ₹${(Number(r.hire_amount) || 0).toLocaleString("en-IN")}, Advance ₹${(Number(r.advance_paid) || 0).toLocaleString("en-IN")}${rExtras(r) > 0 ? `, Extra Charges ₹${rExtras(r).toLocaleString("en-IN")}` : ""}, *Balance ₹${rBal(r).toLocaleString("en-IN")}*${podLinks[r.id] ? `\n   📄 POD: ${podLinks[r.id]}` : ""}`),
              "",
              ...(totEx > 0 ? [`Total Extra Charges: ₹${totEx.toLocaleString("en-IN")} (Unloading ₹${totUnl.toLocaleString("en-IN")} · Challan ₹${totChg.toLocaleString("en-IN")} · Extra Wt ₹${totWt.toLocaleString("en-IN")} · Extra Dly ₹${totDly.toLocaleString("en-IN")})`] : []),
              `*Total Balance Due: ₹${list.reduce((s, r) => s + rBal(r), 0).toLocaleString("en-IN")} — ${list.length} challan(s)*`,
              "",
              "— Nisha Transport Corporation",
            ].join("\n");
            window.open(`https://wa.me/${vphone.length === 10 ? "91" + vphone : vphone}?text=${encodeURIComponent(msg)}`, "_blank");
          };
          return (
            <span className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-800" data-testid="vendor-hire-total">
              "{q}": Total Hire {inr(hire)} · Balance Due {inr(bal)} · {(shown || []).length} challan(s)
              {vphone ? (
                <button
                  type="button"
                  className="grid h-6 w-6 place-items-center rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                  title="WhatsApp balance summary to vendor"
                  onClick={sendVendorBalance}
                  data-testid="vendor-balance-wa-btn"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </span>
          );
        })() : null;
        const exp = (rows || []).filter((r) => isExpired(r.ins_valid_upto));
        const soon = (rows || []).filter((r) => {
          const d = r.ins_valid_upto;
          if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(String(d)) || isExpired(d)) return false;
          return (new Date(d) - new Date(today())) / 86400000 <= 30;
        });
        const shareInsurance = () => {
          const line = (r, tag) => `• ${r.vehicle_no || "—"} — ${r.owner_name || r.vendor_broker_name || "—"} — Policy ${r.policy_no || "—"} — ${tag} ${fmtDate(r.ins_valid_upto)} (${r.engaged_office || r.office || "Chennai"})`;
          const msg = [
            "⚠ *NTC — Vehicle Insurance Alert*",
            ...(exp.length ? ["", `Expired (${exp.length}):`, ...exp.slice(0, 10).map((r) => line(r, "expired"))] : []),
            ...(soon.length ? ["", `Expiring within 30 days (${soon.length}):`, ...soon.slice(0, 10).map((r) => line(r, "expires"))] : []),
            "",
            "Please renew the insurance immediately.",
          ].join("\n");
          const branches = [...new Set([...exp, ...soon].map((r) => r.engaged_office || r.office).filter(Boolean))];
          const num = branches.length === 1 ? contacts[branches[0]] || "" : "";
          window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
        };
        return (
          <div className="flex items-center gap-2">
            {srchBanner}
            <Button variant="outline" onClick={() => setBlankRows(rows || [])} data-testid="blank-challan-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Blank Print
            </Button>
            <Button variant="outline" onClick={() => setRegisterOpen(true)} data-testid="vehicle-payments-register-btn">
              <IndianRupee className="mr-2 h-4 w-4" /> Payments Register
            </Button>
            <Button variant="outline" onClick={() => setRemRegOpen(true)} data-testid="reminder-register-btn">
              <BellRing className="mr-2 h-4 w-4" /> Reminders
            </Button>
            <Button variant="outline" onClick={() => setDueRegOpen(true)} data-testid="balance-due-register-btn">
              <IndianRupee className="mr-2 h-4 w-4" /> Balance Due
            </Button>
            <Button variant="outline" onClick={() => setPodPendOpen(true)} data-testid="pod-pending-btn">
              <Paperclip className="mr-2 h-4 w-4" /> POD Pending
            </Button>
            <Button variant="outline" onClick={() => setAckRegOpen(true)} data-testid="pod-ack-register-btn">
              <Paperclip className="mr-2 h-4 w-4" /> POD Ack Register
            </Button>
            <Button variant="outline" onClick={() => setOldVehOpen(true)} data-testid="old-vehicle-register-btn">
              <AlertTriangle className="mr-2 h-4 w-4" /> Old Vehicles
            </Button>
            <Button variant="outline" onClick={() => setDocExpOpen(true)} data-testid="doc-expiry-btn">
              <MessageCircle className="mr-2 h-4 w-4" /> Docs Expiry
            </Button>
            <Button variant="outline" onClick={() => setRouteRegOpen(true)} data-testid="route-rate-register-btn">
              <IndianRupee className="mr-2 h-4 w-4" /> Route Rates
            </Button>
            <Button variant="outline" onClick={() => setChargesRep(rows || [])} data-testid="extra-charges-report-btn">
              <IndianRupee className="mr-2 h-4 w-4" /> Charges Report
            </Button>
            {isAdmin && (() => {
              const pend = (rows || []).filter((r) => r.charge_approval_status === "Pending");
              const stale = pend.filter((r) => {
                const t = Date.parse(r.charge_submitted_at || r.created_at || r.date || "");
                return !isNaN(t) && (Date.now() - t) / 86400000 > 2;
              }).length;
              return (
                <>
                <Button
                  variant="outline"
                  className={stale ? "animate-pulse border-red-300 bg-red-50 text-red-700 hover:bg-red-100" : pend.length ? "border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100" : ""}
                  title={stale ? `${stale} approval(s) waiting more than 2 days — decide them before the queue goes stale` : pend.length ? "Over-limit extra charges waiting for your approval" : "Review past extra-charge approve/reject decisions"}
                  onClick={() => setChargeApprovals({ rows, load })}
                  data-testid="charge-approvals-btn"
                >
                  <AlertTriangle className="mr-2 h-4 w-4" /> Charge Approvals ({pend.length}){stale ? ` • ${stale} stale` : ""}
                </Button>
                {stale > 0 && new Date().getDay() === 1 && (
                  <Button
                    variant="outline"
                    className="border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                    title="It's Monday — WhatsApp yourself the list of approvals that sat in the queue over 2 days"
                    onClick={async () => {
                      const exT = (r) => (Number(r.unloading_charge) || 0) + (Number(r.challan_charge) || 0) + (Number(r.extra_weight_charge) || 0) + (Number(r.extra_delivery_charge) || 0);
                      const aged = (r) => Math.floor((Date.now() - Date.parse(r.charge_submitted_at || r.created_at || r.date || "")) / 86400000);
                      const list = pend.filter((r) => aged(r) > 2);
                      let mob = "";
                      try {
                        const resp = await api.get("/settings/otp-mobile");
                        mob = String(resp.data?.mobile || "").replace(/\D/g, "");
                      } catch {}
                      const target = mob ? (mob.length === 10 ? "91" + mob : mob) : "";
                      const msg = [
                        "*NTC — MONDAY STALE APPROVALS* ⚠",
                        `${list.length} challan${list.length === 1 ? "" : "s"} waiting over 2 days for extra-charge approval:`,
                        "",
                        ...list.slice(0, 15).map((r) => `• Challan ${String(r.challan_no || "—").replace(/^VHC[-\s]?/i, "")} — ${r.vehicle_no || "—"} — ${inr(exT(r))} — waiting ${aged(r)}d (${r.engaged_office || r.office || "—"})`),
                        "",
                        "Open Vehicle Hire Challan → Charge Approvals to decide.",
                      ].join("\n");
                      window.open(target ? `https://wa.me/${target}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
                    }}
                    data-testid="monday-stale-wa-btn"
                  >
                    <MessageCircle className="mr-2 h-4 w-4" /> Monday: WhatsApp Stale List
                  </Button>
                )}
                </>
              );
            })()}
            {isAdmin && (rows || []).some((r) => !r.engaged_office) && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                title="Set the blank Branch column on old challans from each challan's engaged office in one tap"
                onClick={async () => {
                  if (!window.confirm("Fill the blank Branch on old challans from their engaged office? This updates them in one go.")) return;
                  try {
                    const { data } = await api.post("/hire-challans/backfill-branch");
                    toast.success(`${data.updated} challan(s) got their branch filled`);
                    load();
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail));
                  }
                }}
                data-testid="backfill-branch-btn"
              >
                Fill Blank Branch
              </Button>
            )}
            {isAdmin && OFFICES.map((b) => {
              const n = (rows || []).filter((r) => (r.engaged_office || r.office || "") === b).length;
              const active = fvals?.branch === b;
              return n ? (
                <button
                  type="button"
                  key={b}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                  title={active ? "Tap to show all branches again" : `Tap to show only ${b}'s challans`}
                  onClick={() => setFvals((s) => ({ ...s, branch: active ? "All Branches" : b }))}
                  data-testid={`vhc-branch-count-${b.toLowerCase()}`}
                >
                  {b} <b className={active ? "" : "text-primary"}>{n}</b>
                </button>
              ) : null;
            })}
            {!isAdmin && (
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700" data-testid="vhc-branch-lock-pill" title="Staff see only their own branch's challans — the admin sees all branches">
                Showing your branch: {user?.office || "—"}
              </span>
            )}
            <PadLogButton />
            {(() => {
              const rej = (rows || []).filter((r) => r.charge_approval_status === "Rejected");
              return rej.length ? (
                <button
                  type="button"
                  className="flex items-center gap-1.5 rounded-md bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
                  title="Challans whose extra charges the admin rejected — tap to see the notes, then edit each challan to correct its charges"
                  onClick={() => {
                    const lines = rej.slice(0, 15).map((r) => `• Challan ${String(r.challan_no || "—").replace(/^VHC[-\s]?/i, "")} — ${r.vehicle_no || "—"} — ${inr((Number(r.unloading_charge) || 0) + (Number(r.challan_charge) || 0) + (Number(r.extra_weight_charge) || 0) + (Number(r.extra_delivery_charge) || 0))}\n  Admin's note: ${r.charge_reject_note || "(no note)"}`);
                    window.alert(`⛔ REJECTED EXTRA CHARGES (${rej.length})\n\n${lines.join("\n\n")}\n\nEdit each challan to correct its charges and resubmit.`);
                  }}
                  data-testid="charges-rejected-chip"
                >
                  <AlertTriangle className="h-4 w-4" /> {rej.length} charges rejected
                </button>
              ) : null;
            })()}
            {exp.length ? (
              <span className="flex items-center gap-1.5 rounded-md bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700" data-testid="insurance-alert">
                <AlertTriangle className="h-4 w-4" /> {exp.length} insurance expired
              </span>
            ) : null}
            {(() => {
              const fcExp = (rows || []).filter((r) => isExpired(String(r.fc_valid_upto || "").slice(0, 10)));
              return fcExp.length ? (
                <span className="flex items-center gap-1.5 rounded-md bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700" title="Vehicles whose Fitness Certificate (FC) has lapsed — renew before despatch" data-testid="fc-expired-alert">
                  <AlertTriangle className="h-4 w-4" /> {fcExp.length} FC lapsed
                </span>
              ) : null;
            })()}
            {(() => {
              const seen = new Set();
              (rows || []).forEach((r) => {
                const k = String(r.vehicle_no || "").replace(/\s+/g, "").toUpperCase();
                if (k) seen.add(k);
              });
              const miss = [...seen].filter((k) => {
                const t = docsSummary[k] || [];
                return !t.includes("RC") || !t.includes("FC");
              });
              return miss.length ? (
                <span className="flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-700" title="Vehicles without an RC or FC copy in the Document Vault — collect from drivers at loading" data-testid="missing-docs-alert">
                  <Paperclip className="h-4 w-4" /> {miss.length} veh missing RC/FC
                </span>
              ) : null;
            })()}
            {(() => {
              const out = (rows || []).filter((r) => !r.pod_ack_received);
              const daysOf = (r) => {
                const ld = String(r.loading_date || r.date || "").slice(0, 10);
                return /^\d{4}-\d{2}-\d{2}$/.test(ld) ? Math.floor((new Date(today()) - new Date(ld)) / 86400000) : 0;
              };
              const agedList = out.filter((r) => daysOf(r) > 15).sort((a, b) => daysOf(b) - daysOf(a));
              const agedWa = () => {
                const msg = [
                  "⚠ *NTC — Signed PODs OUT 15+ Days*",
                  `${agedList.length} challan(s) pending POD return:`,
                  "",
                  ...agedList.slice(0, 20).map((r, i) => {
                    const no = String(r.challan_no || "").replace(/^VHC[-\s]?/i, "");
                    const parts = [
                      `${i + 1}. Challan ${no} — ${r.vehicle_no || "—"} — *out ${daysOf(r)} days*`,
                      `   Driver: ${r.driver_name || "—"}${r.driver_phone ? ` (${r.driver_phone})` : ""}`,
                      `   Owner: ${r.owner_name || "—"}${r.owner_mobile ? ` (${r.owner_mobile})` : ""}`,
                      `   Vendor/Broker: ${r.vendor_broker_name || "—"}${r.vendor_mobile ? ` (${r.vendor_mobile})` : ""}`,
                    ];
                    return parts.join("\n");
                  }),
                  ...(agedList.length > 20 ? [`…and ${agedList.length - 20} more`] : []),
                  "",
                  "Please chase the drivers for signed POD return — balance is held until POD is back.",
                  "— Nisha Transport Corporation",
                ].join("\n");
                const digits = String(localStorage.getItem("ntc_company_mobile") || "").replace(/\D/g, "");
                const target = digits ? (digits.length === 10 ? "91" + digits : digits) : "";
                window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
              };
              return out.length ? (
                <>
                  <span className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold ${agedList.length ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`} title="Challans whose signed POD has not come back from the driver — use the chase button on each row" data-testid="pod-ack-out-pill">
                    <Paperclip className="h-4 w-4" /> {out.length} POD ack out{agedList.length ? ` · ${agedList.length} over 15d` : ""}
                  </span>
                  {agedList.length ? (
                    <Button size="sm" className="h-8 bg-emerald-600 text-white hover:bg-emerald-700" title="WhatsApp summary of all 15-day-plus POD-out challans with driver, owner and vendor numbers" onClick={agedWa} data-testid="pod-ack-aged-wa-btn">
                      <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
                    </Button>
                  ) : null}
                </>
              ) : null;
            })()}
            {exp.length + soon.length > 0 ? (
              <Button size="sm" className="h-8 bg-emerald-600 text-white hover:bg-emerald-700" onClick={shareInsurance} data-testid="insurance-whatsapp-btn">
                <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
              </Button>
            ) : null}
          </div>
        );
      }}
      rowActions={(r) => (
        <>
          {Number(r.credit_note) > 0 ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-amber-600"
              title={`Print Credit Note voucher (${inr(r.credit_note)})`}
              onClick={() => printNoteVoucher({ date: r.date || r.loading_date, party: r.vendor_broker_name || r.owner_name, ref: r.challan_no, amount: r.credit_note, reason: r.credit_note_reason, branch: r.engaged_office || r.office }, "credit")}
              data-testid="print-cn-voucher-btn"
            >
              <StickyNote className="h-4 w-4" />
            </Button>
          ) : null}
          {r.vendor_broker_name ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-indigo-600"
              title={`Open ${r.vendor_broker_name} ledger`}
              onClick={() => navigate(`/vendor-ledger?vendor=${encodeURIComponent(r.vendor_broker_name)}`)}
              data-testid="challan-vendor-ledger-btn"
            >
              <BookOpen className="h-4 w-4" />
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-emerald-600"
            title="Vehicle payments (Hire / Advance / Balance)"
            onClick={() => setPayTarget(r)}
            data-testid="challan-payments-btn"
          >
            <IndianRupee className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-primary"
            title="Print challan"
            onClick={() => setPrintTarget({ ...r, vendor_code: vCode(r) })}
            data-testid="print-challan-row-btn"
          >
            <Printer className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-emerald-600"
            title="WhatsApp challan summary (hire / advance / balance)"
            onClick={() => challanWa(r)}
            data-testid="challan-wa-btn"
          >
            <MessageCircle className="h-4 w-4" />
          </Button>
        </>
      )}
      selectable
      onPrintSelected={(list) => setPrintTarget(list.map((r) => ({ ...r, vendor_code: vCode(r) })))}
    />
    <Dialog open={remRegOpen} onOpenChange={setRemRegOpen}>
      <DialogContent className="sm:max-w-[820px]" data-testid="reminder-register-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Renewal Reminder Register</DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-3">
          <div className="grid gap-1.5">
            <Label>Month</Label>
            <Input type="month" value={remMonth} onChange={(e) => setRemMonth(e.target.value || today().slice(0, 7))} className="w-44" data-testid="reminder-month" />
          </div>
          <p className="pb-2 text-xs text-muted-foreground" data-testid="reminder-summary">
            {remRows.filter((c) => String(c.ins_reminder_at).startsWith(remMonth)).length} reminder(s) sent this month
          </p>
          <div className="ml-auto flex gap-2 pb-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const list = remRows.filter((c) => String(c.ins_reminder_at).startsWith(remMonth));
                const label = new Date(remMonth + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" });
                printReminderRegister(list, label);
              }}
              data-testid="reminder-register-print-btn"
            >
              <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const list = remRows
                  .filter((c) => String(c.ins_reminder_at).startsWith(remMonth))
                  .sort((a, b) => String(b.ins_reminder_at).localeCompare(String(a.ins_reminder_at)));
                const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
                const csv = [
                  ["Sent On", "Vehicle No.", "Owner/Vendor", "Mobile", "Insurance Upto", "Permit Upto", "Branch"].join(","),
                  ...list.map((c) =>
                    [
                      String(c.ins_reminder_at).slice(0, 10),
                      c.vehicle_no || "",
                      c.owner_name || c.vendor_broker_name || "",
                      c.owner_mobile || c.vendor_mobile || c.driver_phone || "",
                      c.ins_valid_upto || "",
                      c.permit_valid_upto || "",
                      c.engaged_office || c.office || "",
                    ].map(q).join(",")
                  ),
                ].join("\n");
                const a = document.createElement("a");
                a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
                a.download = `reminder-register-${remMonth}.csv`;
                a.click();
                URL.revokeObjectURL(a.href);
              }}
              data-testid="reminder-register-export-btn"
            >
              <Download className="mr-1.5 h-3.5 w-3.5" /> Export CSV
            </Button>
          </div>
        </div>
        <div className="max-h-[360px] overflow-auto rounded border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted">
              <tr className="text-left">
                <th className="p-2">Sent On</th>
                <th className="p-2">Vehicle No.</th>
                <th className="p-2">Owner</th>
                <th className="p-2">Mobile</th>
                <th className="p-2">Insurance Valid Upto</th>
                <th className="p-2">Permit Valid Upto</th>
                <th className="p-2">Branch</th>
              </tr>
            </thead>
            <tbody>
              {remRows.filter((c) => String(c.ins_reminder_at).startsWith(remMonth)).sort((a, b) => String(b.ins_reminder_at).localeCompare(String(a.ins_reminder_at))).map((c) => (
                <tr key={c.id} className="border-t" data-testid="reminder-register-row">
                  <td className="whitespace-nowrap p-2 font-semibold">{fmtDate(String(c.ins_reminder_at).slice(0, 10))}</td>
                  <td className="p-2 font-bold">{c.vehicle_no || "—"}</td>
                  <td className="p-2">{c.owner_name || c.vendor_broker_name || "—"}</td>
                  <td className="whitespace-nowrap p-2">{c.owner_mobile || c.vendor_mobile || c.driver_phone || "—"}</td>
                  <td className={`whitespace-nowrap p-2 ${isExpired(c.ins_valid_upto) ? "font-bold text-red-600" : ""}`}>{c.ins_valid_upto ? fmtDate(c.ins_valid_upto) : "—"}</td>
                  <td className={`whitespace-nowrap p-2 ${isExpired(c.permit_valid_upto) ? "font-bold text-red-600" : ""}`}>{c.permit_valid_upto ? fmtDate(c.permit_valid_upto) : "—"}</td>
                  <td className="p-2">{c.engaged_office || c.office || "—"}</td>
                </tr>
              ))}
              {remRows.filter((c) => String(c.ins_reminder_at).startsWith(remMonth)).length === 0 && (
                <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">No reminders sent in this month.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
    <Dialog open={ackRegOpen} onOpenChange={setAckRegOpen}>
      <DialogContent className="sm:max-w-[640px]" data-testid="pod-ack-register-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">POD Ack Register</DialogTitle>
        </DialogHeader>
        <div className="flex gap-1">
          {[["month", "Month Sheet"], ["score", `Vendor Scorecard (${vendorScore.length})`]].map(([k, l]) => (
            <button
              key={k}
              type="button"
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${ackView === k ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              onClick={() => setAckView(k)}
              data-testid={`ack-view-${k}`}
            >
              {l}
            </button>
          ))}
        </div>
        {ackView === "month" ? (
          <div className="flex flex-wrap items-end gap-3">
            <div className="grid gap-1.5">
              <Label>Month (challan date)</Label>
              <Input type="month" value={ackMonth} onChange={(e) => setAckMonth(e.target.value || today().slice(0, 7))} className="w-44" data-testid="pod-ack-month" />
            </div>
            <p className="pb-2 text-xs text-muted-foreground" data-testid="pod-ack-summary">
              {ackList.length} challan(s) · <span className="font-bold text-emerald-700">{ackList.filter((c) => c.pod_ack_received).length} POD back</span> · <span className="font-bold text-red-700">{ackList.filter((c) => !c.pod_ack_received).length} still out</span>
            </p>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto mb-1"
              disabled={!ackList.length}
              onClick={() => printPodAckRegister(ackList, new Date(ackMonth + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" }))}
              data-testid="pod-ack-register-print-btn"
            >
              <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
            </Button>
          </div>
        ) : (
          <div data-testid="vendor-ack-scorecard">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Fastest POD returners first — slow vendors (avg over 15 days) stand out in red.</p>
              <Button variant="outline" size="sm" disabled={!vendorScore.length} onClick={() => printVendorAckScorecard(vendorScore)} data-testid="vendor-scorecard-print-btn">
                <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
              </Button>
            </div>
            <div className="max-h-[360px] overflow-auto rounded-md border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-100">
                  <tr className="text-left">
                    <th className="p-2">Rank</th>
                    <th className="p-2">Vendor / Broker / Owner</th>
                    <th className="p-2 text-right">Challans</th>
                    <th className="p-2 text-right">POD Back</th>
                    <th className="p-2 text-right">Avg Days to Return</th>
                    <th className="p-2 text-right">Still Out</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorScore.length ? vendorScore.map((v, i) => (
                    <tr key={v.name} className={`border-t ${v.avg != null && v.avg > 15 ? "bg-red-50" : ""}`} data-testid="vendor-score-row">
                      <td className="p-2 font-bold">{i + 1}{i === 0 && v.avg != null ? " 🏆" : ""}</td>
                      <td className="p-2 font-semibold">{v.name}</td>
                      <td className="p-2 text-right">{v.total}</td>
                      <td className="p-2 text-right text-emerald-700">{v.back}</td>
                      <td className={`p-2 text-right font-bold ${v.avg != null && v.avg > 15 ? "text-red-700" : "text-slate-700"}`}>{v.avg != null ? `${v.avg} d` : "—"}</td>
                      <td className={`p-2 text-right font-bold ${v.out ? "text-amber-700" : "text-slate-400"}`}>{v.out || "—"}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No vendor data yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    <Dialog open={dueRegOpen} onOpenChange={setDueRegOpen}>
      <DialogContent className="sm:max-w-[880px]" data-testid="balance-due-register-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Balance Due Register — Settlement Sheet</DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-3">
          <div className="grid gap-1.5">
            <Label>{dueView === "due" ? "Month (challan date)" : "Month (settled on)"}</Label>
            <Input type="month" value={dueMonth} onChange={(e) => setDueMonth(e.target.value || today().slice(0, 7))} className="w-44" data-testid="balance-due-month" />
          </div>
          <div className="flex gap-1 pb-1">
            {[["due", `Pending Due (${dueList.length})`], ["settled", `Settled (${settledList.length})`]].map(([k, l]) => (
              <button
                key={k}
                type="button"
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${dueView === k ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                onClick={() => setDueView(k)}
                data-testid={`due-view-${k}`}
              >
                {l}
              </button>
            ))}
          </div>
          <p className="pb-2 text-xs text-muted-foreground" data-testid="balance-due-summary">
            {dueView === "due"
              ? `${dueList.length} challan(s) unpaid · Total due ${inr(dueList.reduce((a, c) => a + dueAmtOf(c), 0))}`
              : `${settledList.length} challan(s) settled · Total settled ${inr(settledList.reduce((a, c) => a + (Number(c.balance) || 0), 0))}`}
          </p>
          <div className="ml-auto mb-1 flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={dueView === "due" ? !dueList.length : !settledList.length}
              onClick={() => {
                const label = new Date(dueMonth + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" });
                if (dueView === "due") printBalanceDueRegister(dueList, label);
                else printSettlementRegister(settledList, label);
              }}
              data-testid="balance-due-print-btn"
            >
              <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
            </Button>
            {dueView === "due" && (
            <Button
              size="sm"
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              disabled={!dueList.length}
              onClick={() => {
                const label = new Date(dueMonth + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" });
                const sorted = [...dueList].sort((a, b) => dueAmtOf(b) - dueAmtOf(a));
                const msg = [
                  `*NTC — Balance Due Register (${label})*`,
                  `Challans pending: ${sorted.length} | Total due: ${inr(sorted.reduce((a, c) => a + dueAmtOf(c), 0))}`,
                  "",
                  ...sorted.slice(0, 15).map((c) => `• ${String(c.challan_no || "").replace(/^VHC[-\s]?/i, "")} — ${c.vehicle_no || "—"} — ${c.vendor_broker_name || c.owner_name || "—"} — Due ${inr(dueAmtOf(c))}`),
                  ...(sorted.length > 15 ? [`…and ${sorted.length - 15} more`] : []),
                  "",
                  "Arrange cash before settlement day.",
                ].join("\n");
                const digits = String(localStorage.getItem("ntc_company_mobile") || "").replace(/\D/g, "");
                const target = digits ? (digits.length === 10 ? "91" + digits : digits) : "";
                window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
              }}
              data-testid="balance-due-wa-btn"
            >
              <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
            </Button>
            )}
          </div>
        </div>
        <div className="max-h-[360px] overflow-auto rounded border">
          {dueView === "due" ? (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted">
              <tr className="text-left">
                <th className="p-2">Challan No.</th>
                <th className="p-2">Date</th>
                <th className="p-2">Vehicle No.</th>
                <th className="p-2">Vendor / Owner</th>
                <th className="p-2 text-right">Hire</th>
                <th className="p-2 text-right">Advance</th>
                <th className="p-2 text-right">Paid</th>
                <th className="p-2 text-right">Balance Due</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {dueList.sort((a, b) => dueAmtOf(b) - dueAmtOf(a)).map((c) => (
                <tr key={c.id} className="border-t" data-testid="balance-due-row">
                  <td className="p-2 font-bold">{String(c.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—"}</td>
                  <td className="whitespace-nowrap p-2">{c.date || c.loading_date ? fmtDate(c.date || c.loading_date) : "—"}</td>
                  <td className="p-2 font-semibold">{c.vehicle_no || "—"}</td>
                  <td className="p-2">{c.vendor_broker_name || c.owner_name || "—"}</td>
                  <td className="p-2 text-right tabular-nums">{inr(c.hire_amount)}</td>
                  <td className="p-2 text-right tabular-nums">{inr(c.advance_paid)}</td>
                  <td className="p-2 text-right tabular-nums text-emerald-700">{inr(duePaidOf(c))}</td>
                  <td className="p-2 text-right font-bold tabular-nums text-red-600">{inr(dueAmtOf(c))}</td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="outline" className="h-7 border-emerald-300 px-2.5 text-xs font-bold text-emerald-700 hover:bg-emerald-50" onClick={() => settleChallan(c)} data-testid="challan-settle-btn">
                      <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Settled
                    </Button>
                  </td>
                </tr>
              ))}
              {dueList.length === 0 && (
                <tr><td colSpan={9} className="p-4 text-center text-muted-foreground">No unpaid balances for this month.</td></tr>
              )}
            </tbody>
          </table>
          ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted">
              <tr className="text-left">
                <th className="p-2">Challan No.</th>
                <th className="p-2">Challan Date</th>
                <th className="p-2">Vehicle No.</th>
                <th className="p-2">Vendor / Owner</th>
                <th className="p-2 text-right">Settled Amount</th>
                <th className="p-2">Settled On</th>
                <th className="p-2">Settled By</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {[...settledList].sort((a, b) => String(b.settled_at || "").localeCompare(String(a.settled_at || ""))).map((c) => (
                <tr key={c.id} className="border-t" data-testid="settled-row">
                  <td className="p-2 font-bold">{String(c.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—"}</td>
                  <td className="whitespace-nowrap p-2">{c.date || c.loading_date ? fmtDate(c.date || c.loading_date) : "—"}</td>
                  <td className="p-2 font-semibold">{c.vehicle_no || "—"}</td>
                  <td className="p-2">{c.vendor_broker_name || c.owner_name || "—"}</td>
                  <td className="p-2 text-right font-bold tabular-nums text-emerald-700">{inr(c.balance)}</td>
                  <td className="whitespace-nowrap p-2">{c.settled_at ? fmtDate(String(c.settled_at).slice(0, 10)) : "—"}</td>
                  <td className="p-2">{c.settled_by || "—"}</td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs font-bold text-slate-600" onClick={() => settleChallan(c)} data-testid="challan-reopen-btn">
                      Re-open
                    </Button>
                  </td>
                </tr>
              ))}
              {settledList.length === 0 && (
                <tr><td colSpan={8} className="p-4 text-center text-muted-foreground">No challans settled in this month.</td></tr>
              )}
            </tbody>
          </table>
          )}
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={oldVehOpen} onOpenChange={setOldVehOpen}>
      <DialogContent className="sm:max-w-[920px]" data-testid="old-vehicle-register-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Old Vehicle Register — 15+ Years</DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground" data-testid="old-vehicle-summary">
            {oldVehList.length} engaged vehicle(s) with make year older than 15 years — verify route permits before despatch.
          </p>
          <Button variant="outline" size="sm" disabled={!oldVehList.length} onClick={() => printOldVehicleRegister(oldVehList)} data-testid="old-vehicle-print-btn">
            <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
          </Button>
        </div>
        <div className="max-h-[380px] overflow-auto rounded border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted">
              <tr className="text-left">
                <th className="p-2">Vehicle No.</th>
                <th className="p-2">Make Year</th>
                <th className="p-2">Age</th>
                <th className="p-2">Owner / Vendor</th>
                <th className="p-2">Last Challan</th>
                <th className="p-2">Route</th>
                <th className="p-2">Permit Upto</th>
                <th className="p-2">FC Upto</th>
              </tr>
            </thead>
            <tbody>
              {oldVehList.map((c) => {
                const y = makeYearOf(c);
                const age = new Date().getFullYear() - y;
                const fcLapsed = c.fc_valid_upto && String(c.fc_valid_upto).slice(0, 10) < today();
                return (
                  <tr key={c.id} className="border-t" data-testid="old-vehicle-row">
                    <td className="p-2 font-bold">{c.vehicle_no || "—"}</td>
                    <td className="p-2 font-semibold">{y}</td>
                    <td className="p-2 font-bold text-red-600">{age} yrs</td>
                    <td className="p-2">{c.owner_name || c.vendor_broker_name || "—"}{c.owner_mobile ? ` (${c.owner_mobile})` : ""}</td>
                    <td className="p-2">{String(c.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—"}</td>
                    <td className="p-2">{[c.origin, c.destination].filter(Boolean).join(" → ") || "—"}</td>
                    <td className="whitespace-nowrap p-2">{c.permit_valid_upto ? fmtDate(c.permit_valid_upto) : "—"}</td>
                    <td className={`whitespace-nowrap p-2 ${fcLapsed ? "font-bold text-red-600" : ""}`}>{c.fc_valid_upto ? `${fmtDate(c.fc_valid_upto)}${fcLapsed ? " (lapsed)" : ""}` : "—"}</td>
                  </tr>
                );
              })}
              {oldVehList.length === 0 && (
                <tr><td colSpan={8} className="p-4 text-center text-muted-foreground">No engaged vehicles older than 15 years.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={docExpOpen} onOpenChange={setDocExpOpen}>
      <DialogContent className="sm:max-w-[880px]" data-testid="doc-expiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Document Expiry — Renewal Reminders</DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground" data-testid="doc-expiry-summary">
            {docExpGroups.reduce((a, g) => a + g.vehicles.length, 0)} vehicle(s) with lapsed or soon-expiring (≤30 days) Insurance / Permit / FC / DL across {docExpGroups.length} owner(s) — Document Vault dates included, even without a challan.
          </p>
          <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" disabled={!docExpGroups.length} onClick={docExpWaAll} data-testid="doc-expiry-wa-all-btn">
            <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> Full List → My WhatsApp
          </Button>
        </div>
        <div className="max-h-[380px] overflow-auto rounded border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted">
              <tr className="text-left">
                <th className="p-2">Owner / Vendor</th>
                <th className="p-2">Mobile</th>
                <th className="p-2">Vehicles &amp; Documents <span className="font-normal text-muted-foreground">(red = lapsed · amber = expiring ≤30d)</span></th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {docExpGroups.map((grp) => (
                <tr key={grp.owner + grp.mobile} className="border-t align-top" data-testid="doc-expiry-row">
                  <td className="p-2 font-semibold">{grp.owner}</td>
                  <td className="whitespace-nowrap p-2">{grp.mobile || "—"}</td>
                  <td className="p-2">
                    {grp.vehicles.map((v) => (
                      <div key={v.vehicle_no} className="mb-1">
                        <span className="font-bold">{v.vehicle_no || "—"}</span>{" — "}
                        {v.lapsed.map(([n, d]) => (
                          <span key={n} className="mr-1 inline-flex rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">
                            {n} · {fmtDate(String(d).slice(0, 10))}
                          </span>
                        ))}
                        {v.soon.map(([n, d]) => (
                          <span key={"s" + n} className="mr-1 inline-flex rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700" data-testid="doc-soon-chip">
                            {n} · expires {fmtDate(String(d).slice(0, 10))}
                          </span>
                        ))}
                      </div>
                    ))}
                  </td>
                  <td className="p-2 text-right">
                    <Button size="sm" className="h-7 bg-emerald-600 px-2.5 text-xs text-white hover:bg-emerald-700" onClick={() => docExpWa(grp)} data-testid="doc-expiry-wa-btn">
                      <MessageCircle className="mr-1 h-3.5 w-3.5" /> WhatsApp
                    </Button>
                  </td>
                </tr>
              ))}
              {docExpGroups.length === 0 && (
                <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">No lapsed insurance, permit or FC — all documents are current.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>

    {vaultTarget ? (
      <VehicleVaultDialog vehicleNo={vaultTarget.vehicle_no} onClose={() => setVaultTarget(null)} onChanged={() => setReloadKey((k) => k + 1)} />
    ) : null}

    <Dialog open={routeRegOpen} onOpenChange={setRouteRegOpen}>
      <DialogContent className="sm:max-w-[920px]" data-testid="route-rate-register-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Route Rate Register — Hire Benchmarks</DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground" data-testid="route-rate-summary">
            {routeStats.length} route(s) with paid hires — average, range and last-paid for rate negotiations.
          </p>
          <Button variant="outline" size="sm" disabled={!routeStats.length} onClick={() => printRouteRateRegister(routeStats)} data-testid="route-rate-print-btn">
            <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
          </Button>
        </div>
        <div className="max-h-[400px] overflow-auto rounded border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted">
              <tr className="text-left">
                <th className="p-2">Route</th>
                <th className="p-2 text-center">Trips</th>
                <th className="p-2 text-right">Avg Hire</th>
                <th className="p-2 text-right">Min</th>
                <th className="p-2 text-right">Max</th>
                <th className="p-2 text-right">Last Paid</th>
                <th className="p-2">Last Vehicle · Date</th>
                <th className="p-2 text-right">Saved Rate</th>
                <th className="p-2 text-center">Avg vs Last Month</th>
              </tr>
            </thead>
            <tbody>
              {routeStats.map((r) => (
                <tr key={r.origin + r.destination} className="border-t" data-testid="route-rate-row">
                  <td className="p-2 font-bold">{r.origin} → {r.destination}</td>
                  <td className="p-2 text-center">{r.trips}</td>
                  <td className="p-2 text-right font-bold">{inr(r.avg)}</td>
                  <td className="p-2 text-right">{inr(r.min)}</td>
                  <td className="p-2 text-right">{inr(r.max)}</td>
                  <td className="p-2 text-right">{inr(r.last?.hire_amount)}</td>
                  <td className="whitespace-nowrap p-2">{r.last?.vehicle_no || "—"} · {fmtDate(String(r.last?.loading_date || r.last?.date || "").slice(0, 10))}</td>
                  <td className="p-2 text-right">{r.saved ? inr(r.saved) : "—"}</td>
                  <td className="whitespace-nowrap p-2 text-center" data-testid="route-trend-cell">
                    {r.trend === null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : r.trend === 0 ? (
                      <span className="text-muted-foreground">No change</span>
                    ) : (
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${r.trend > 0 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {r.trend > 0 ? "▲ +" : "▼ −"}{inr(Math.abs(r.trend))}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {routeStats.length === 0 && (
                <tr><td colSpan={9} className="p-4 text-center text-muted-foreground">No routes with paid hires yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
